import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { Loader2, LogOut, Inbox, Users, Settings, Search, ShieldCheck, Trash2, UserPlus, KeyRound, Check, Download, Mail, MessageSquare, X, Copy, Sparkles, Send, Eye, EyeOff, Menu, ArrowUpRight, ChevronDown, Archive, ArchiveRestore, CheckCircle2, Target, Save, BarChart3, FileText, FileSpreadsheet, Lock, HeartHandshake, Sun, Moon, ListPlus, Undo2, Clock, History, AlertTriangle } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { cn } from "@/lib/utils";
import { adminLogin, adminMe, getSubmissions, getAdmins, createAdmin, updateAdmin, deleteAdmin, changePassword, markReviewed, logSubmissionAction, closeSubmission, reopenSubmission, archiveSubmission, unarchiveSubmission, draftResponse, sendResponse, archiveResponse, getDonationReport, setDonationGoal, getGoalRecommendation, getDonationsList, sendMonthlyReport, getSummaries, archiveSummary, getAudit, auditReportUrl, getAuditReports, archiveAuditReport, auditReportContentUrl, getReports, getReportBlob, archiveReport, submissionsExportUrl, donationsExportUrl, reportContentUrl, summaryAttachmentUrl } from "@/lib/api";

/* ============================ Design tokens ============================ */
const GOLD = "#D9A93E";
const EMAIL_HEADER_IMG = `${process.env.REACT_APP_BACKEND_URL}/api/static/email/header.jpg`;
const EMAIL_FOOTER_IMG = `${process.env.REACT_APP_BACKEND_URL}/api/static/email/footer.png`;
const btnGold = "inline-flex items-center justify-center gap-2 rounded-lg bg-gradient-to-r from-[#D9A93E] to-[#E0B25A] px-4 py-2.5 text-sm font-semibold text-[#050B14] border-t border-white/30 shadow-[0_0_15px_-3px_rgba(217,169,62,0.4)] transition-[transform,box-shadow,background-color] duration-300 hover:from-[#E0B25A] hover:to-[#E7C988] hover:shadow-[0_0_25px_-3px_rgba(217,169,62,0.6)] hover:-translate-y-0.5 active:translate-y-0 disabled:opacity-60 disabled:hover:translate-y-0 disabled:hover:shadow-[0_0_15px_-3px_rgba(217,169,62,0.4)]";
const btnSecondary = "inline-flex items-center justify-center gap-2 rounded-lg border border-[var(--a-border)] bg-[var(--a-btn2)] px-4 py-2.5 text-sm font-medium text-[var(--a-text)] shadow-sm transition-[transform,border-color,background-color] duration-300 hover:bg-[var(--a-hover)] hover:border-[#D9A93E]/50 hover:-translate-y-0.5 active:translate-y-0";
const btnChip = "inline-flex items-center gap-1.5 rounded-lg border border-[var(--a-border)] bg-[var(--a-surface2)] px-3.5 py-2 text-sm font-medium text-[var(--a-text2)] transition-colors hover:border-[var(--a-border2)] hover:text-[var(--a-text)]";
const card = "rounded-xl border border-[var(--a-border)] bg-gradient-to-b from-[var(--a-surface)] to-[var(--a-surface2)] shadow-[0_8px_30px_-12px_rgba(0,0,0,0.6)] ring-1 ring-white/5 transition-colors duration-300 hover:border-[var(--a-border2)]";
const fieldCls = "h-11 rounded-lg border-[var(--a-border)] bg-[var(--a-field)] text-base text-[var(--a-text)] placeholder:text-[var(--a-text-dim)] shadow-inner transition-colors duration-200 focus-visible:border-[#D9A93E] focus-visible:ring-2 focus-visible:ring-[#D9A93E]/40";
const labelCls = "block text-[11px] font-semibold uppercase tracking-[0.14em] text-[var(--a-text-muted)]";

function AdminThemeToggle({ theme, onToggle, className = "" }) {
  const isDark = theme === "dark";
  return (
    <button type="button" onClick={onToggle} data-testid="admin-theme-toggle"
      aria-label={isDark ? "Switch to light mode" : "Switch to dark mode"}
      title={isDark ? "Switch to light mode" : "Switch to dark mode"}
      className={`relative grid h-9 w-9 shrink-0 place-items-center rounded-lg border border-[var(--a-border)] bg-[var(--a-chip)] text-[var(--a-text-muted)] transition-colors duration-200 hover:border-[#D9A93E]/50 hover:text-[var(--a-gold-ink)] ${className}`}>
      <Sun className={`absolute h-[17px] w-[17px] transition-all duration-300 ${isDark ? "rotate-90 scale-0 opacity-0" : "rotate-0 scale-100 opacity-100"}`} strokeWidth={1.75} />
      <Moon className={`absolute h-[17px] w-[17px] transition-all duration-300 ${isDark ? "rotate-0 scale-100 opacity-100" : "-rotate-90 scale-0 opacity-0"}`} strokeWidth={1.75} />
    </button>
  );
}

function AdminSelect({ value, onValueChange, options, triggerClassName = "", testId, ariaLabel, placeholder }) {
  return (
    <Select value={value} onValueChange={onValueChange}>
      <SelectTrigger data-testid={testId} aria-label={ariaLabel}
        className={cn("rounded-lg border border-[var(--a-border)] bg-[var(--a-field)] text-[var(--a-text)] shadow-inner transition-colors duration-200 hover:border-[var(--a-border2)] focus:border-[#D9A93E] focus:ring-2 focus:ring-[#D9A93E]/40 data-[state=open]:border-[#D9A93E] [&>svg]:opacity-80 [&>svg]:text-[var(--a-gold-ink)]", triggerClassName)}>
        <SelectValue placeholder={placeholder} />
      </SelectTrigger>
      <SelectContent className="max-h-72 rounded-xl border border-[var(--a-border)] bg-[var(--a-surface)] p-1.5 text-[var(--a-text)] shadow-[0_20px_50px_-15px_rgba(0,0,0,0.55)] ring-1 ring-black/5 backdrop-blur-sm">
        {options.map((o) => (
          <SelectItem key={o.value} value={o.value}
            className="cursor-pointer rounded-lg py-2 pl-2.5 pr-8 text-[var(--a-text2)] transition-colors duration-150 focus:bg-[var(--a-hover)] focus:text-[var(--a-text)] data-[state=checked]:font-semibold data-[state=checked]:text-[var(--a-gold-ink)] [&_svg]:text-[var(--a-gold-ink)]">
            {o.label}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

const CATEGORIES = [
  { id: "volunteer", label: "Volunteers" },
  { id: "partner", label: "Partners" },
  { id: "support_request", label: "Support" },
  { id: "contact", label: "Contact" },
];
const PERMISSIONS = [
  { id: "volunteer", label: "Volunteers", group: "Submissions" },
  { id: "partner", label: "Partners", group: "Submissions" },
  { id: "support_request", label: "Support", group: "Submissions" },
  { id: "contact", label: "Contact", group: "Submissions" },
  { id: "donations", label: "Donations", group: "Donations & Reports" },
  { id: "reports_view", label: "Reports: View", group: "Donations & Reports" },
  { id: "reports_send", label: "Reports: Send", group: "Donations & Reports" },
  { id: "archive", label: "Archive access", group: "Other" },
  { id: "audit", label: "Activity log & audit", group: "Other" },
];
const STATUS_META = {
  new: { label: "New", cls: "bg-sky-400/15 text-sky-300 ring-sky-400/30", dot: "bg-sky-400" },
  reviewed: { label: "Reviewed", cls: "bg-amber-400/15 text-amber-300 ring-amber-400/30", dot: "bg-amber-400" },
  actioned: { label: "Actioned", cls: "bg-emerald-400/15 text-emerald-300 ring-emerald-400/30", dot: "bg-emerald-400" },
  closed: { label: "Closed", cls: "bg-[var(--a-hover)] text-[var(--a-text2)] ring-[var(--a-border)]", dot: "bg-[var(--a-text-dim)]" },
};
// What-was-done options per submission category (multi-select). "Other" adds a free-text note.
const ACTIONS_BY_CATEGORY = {
  volunteer: ["Called applicant", "Left voicemail", "Background check initiated", "Background check cleared", "Interview scheduled", "Interview completed", "Offered position", "Position accepted", "Position declined", "Added to roster", "Follow-up scheduled"],
  partner: ["Called partner", "Left voicemail", "Discovery call held", "Agreement/MOU sent", "Agreement signed", "Partnership approved", "Partnership declined", "Referred to program lead", "Follow-up scheduled"],
  support_request: ["Called requester", "Left voicemail", "Assessed need/eligibility", "Resource/referral provided", "Assistance approved", "Assistance provided", "Escalated to case manager", "Request declined", "Follow-up scheduled"],
  contact: ["Called", "Left voicemail", "Question answered", "Routed to right person/dept", "Added to mailing list", "No action needed", "Follow-up scheduled"],
};
const REMARKS_PLACEHOLDER = {
  volunteer: "e.g., Reached out to the applicant and offered a volunteer position, starting Sept 1, 2026.",
  partner: "e.g., Signed a 12-month partnership MOU; kickoff scheduled for next month.",
  support_request: "e.g., Provided a food-assistance referral and confirmed the family received support on Aug 20, 2026.",
  contact: "e.g., Answered the enquiry by phone and added them to the newsletter list.",
};
// Reasons for closing out a submission, tailored per type. "Other" always requires an explanation.
const CLOSE_REASONS = {
  volunteer: ["Volunteer onboarded", "Not a fit at this time", "No response from applicant", "Referred elsewhere", "Duplicate / withdrawn", "Other"],
  partner: ["Partnership established", "Not a fit at this time", "No response from organization", "Referred elsewhere", "Duplicate / withdrawn", "Other"],
  support_request: ["Support provided", "Referred to a partner/resource", "Unable to assist", "No response from requester", "Duplicate / withdrawn", "Other"],
  contact: ["Inquiry answered", "No response needed", "Spam / not genuine", "Referred elsewhere", "Other"],
};
const DEFAULT_CLOSE_REASONS = ["Resolved", "Referred elsewhere", "No response received", "Unable to assist", "Duplicate / withdrawn", "Other"];
const TYPE_LABEL = { volunteer: "Volunteer", partner: "Partner", support_request: "Support", contact: "Contact" };
const TYPE_COLOR = {
  volunteer: "bg-emerald-400/10 text-emerald-300 ring-emerald-400/20",
  partner: "bg-sky-400/10 text-sky-300 ring-sky-400/20",
  support_request: "bg-amber-400/10 text-amber-300 ring-amber-400/20",
  contact: "bg-violet-400/10 text-violet-300 ring-violet-400/20",
};
const HIDDEN = new Set(["id", "type", "consent", "responses", "history", "follow_up", "archived", "archive_reason", "archive_note", "archived_at", "archived_by", "close_reason", "close_remarks"]);
const errMsg = (e, f) => e?.response?.data?.detail || f;

// Reliably trigger a browser download. Blob/anchor(download) downloads are blocked inside
// sandboxed preview iframes, so we open the file's real (token-authenticated) URL in a new
// top-level tab via a target=_blank anchor, which escapes the sandbox; the server responds
// with Content-Disposition: attachment so the browser downloads it.
const triggerDownload = (url) => {
  const a = document.createElement("a");
  a.href = url;
  a.target = "_blank";
  a.rel = "noopener";
  document.body.appendChild(a);
  a.click();
  setTimeout(() => a.remove(), 1500);
};

const PWD_RULES = [
  { id: "len", label: "At least 10 characters", test: (p) => p.length >= 10 },
  { id: "upper", label: "One uppercase letter", test: (p) => /[A-Z]/.test(p) },
  { id: "lower", label: "One lowercase letter", test: (p) => /[a-z]/.test(p) },
  { id: "digit", label: "One number", test: (p) => /[0-9]/.test(p) },
  { id: "special", label: "One special character (!@#$%…)", test: (p) => /[!@#$%^&*()\-_=+[\]{};:,.?/]/.test(p) },
];
const pwdValid = (p) => PWD_RULES.every((r) => r.test(p));

function PasswordInput({ value, onChange, placeholder, testId, className }) {
  const [show, setShow] = useState(false);
  return (
    <div className="relative">
      <Input type={show ? "text" : "password"} value={value} onChange={onChange} placeholder={placeholder} data-testid={testId} className={`${className} pr-11`} />
      <button type="button" onClick={() => setShow((s) => !s)} data-testid={`${testId}-toggle`} aria-label={show ? "Hide password" : "Show password"} tabIndex={-1}
        className="absolute right-3 top-1/2 -translate-y-1/2 text-[var(--a-text-dim)] transition-colors hover:text-[var(--a-text2)]">
        {show ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
      </button>
    </div>
  );
}

function PolicyChecklist({ pw }) {
  return (
    <ul className="mt-3 grid gap-1.5" data-testid="password-policy">
      {PWD_RULES.map((r) => {
        const ok = r.test(pw);
        return (
          <li key={r.id} className={`flex items-center gap-2 text-xs transition-colors ${ok ? "text-emerald-300" : "text-[var(--a-text-dim)]"}`}>
            <span className={`grid h-4 w-4 place-items-center rounded-full ${ok ? "bg-emerald-400/15" : "bg-white/5"}`}><Check className={`h-3 w-3 ${ok ? "opacity-100" : "opacity-30"}`} /></span>
            {r.label}
          </li>
        );
      })}
    </ul>
  );
}

const fmtDate = (iso) => { try { return new Date(iso).toLocaleString("en-US", { timeZone: "America/New_York", month: "short", day: "numeric", year: "numeric", hour: "numeric", minute: "2-digit", hour12: true, timeZoneName: "short" }); } catch { return iso; } };
const fmtDateOnly = (d) => { try { return new Date(d + "T00:00:00").toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }); } catch { return d; } };
const displayName = (s) => (s.first_name || s.last_name) ? `${s.first_name || ""} ${s.last_name || ""}`.trim() : (s.name || s.organization || s.email || "—");

/* ============================ Collapsible section ============================ */
function Collapse({ label, count, icon: Icon, defaultOpen = true, testId, headerClassName = "text-[var(--a-text-muted)]", children }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div>
      <button type="button" onClick={() => setOpen((o) => !o)} data-testid={testId}
        className={`flex w-full items-center gap-1.5 text-[0.65rem] font-semibold uppercase tracking-wide transition-colors ${headerClassName}`}>
        <ChevronDown className={`h-3.5 w-3.5 shrink-0 transition-transform ${open ? "" : "-rotate-90"}`} />
        {Icon && <Icon className="h-3 w-3 shrink-0" />}
        {label}{count != null && <span className="opacity-70">({count})</span>}
      </button>
      {open && <div className="mt-2">{children}</div>}
    </div>
  );
}

/* ============================ Secure auth shell (login / force change) ============================ */
function AuthLayout({ icon: Icon, title, subtitle, children, testId, theme, onToggleTheme }) {
  return (
    <div className="grid min-h-screen place-items-center bg-[var(--a-bg)] px-4 text-[var(--a-text)]">
      {onToggleTheme && <div className="absolute right-4 top-4"><AdminThemeToggle theme={theme} onToggle={onToggleTheme} /></div>}
      <div className="relative w-full max-w-md">
        <div className="pointer-events-none absolute -inset-px rounded-2xl bg-gradient-to-b from-[#D9A93E]/25 via-transparent to-transparent opacity-40 blur-md" />
        <div className={`${card} relative rounded-2xl p-8 shadow-2xl`} data-testid={testId}>
          <span className="mx-auto grid h-14 w-14 place-items-center rounded-2xl bg-[#D9A93E]/12 text-[var(--a-gold-ink)] ring-1 ring-[#D9A93E]/25"><Icon className="h-7 w-7" strokeWidth={1.75} /></span>
          <h1 className="mt-5 text-center font-heading text-2xl font-semibold tracking-tight">{title}</h1>
          <p className="mx-auto mt-1.5 max-w-xs text-center text-sm text-[var(--a-text-muted)]">{subtitle}</p>
          {children}
        </div>
      </div>
    </div>
  );
}

function LoginView({ onLogin, theme, onToggleTheme }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const submit = async (e) => {
    e.preventDefault(); setError(""); setLoading(true);
    try {
      const data = await adminLogin(email, password);
      onLogin(data, password);
    } catch (err) { setError(errMsg(err, "Unable to sign in. Please try again.")); }
    finally { setLoading(false); }
  };
  return (
    <AuthLayout icon={ShieldCheck} title="Admin Portal" subtitle="Thefrent Foundation · secure access" theme={theme} onToggleTheme={onToggleTheme}>
      <form onSubmit={submit} className="mt-7" data-testid="admin-login-form">
        <label className={labelCls}>Email</label>
        <Input data-testid="admin-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} className={`mt-2 ${fieldCls}`} />
        <label className={`${labelCls} mt-4`}>Password</label>
        <PasswordInput testId="admin-password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="••••••••" className={`mt-2 ${fieldCls}`} />
        {error && <p className="mt-4 rounded-lg border border-rose-500/20 bg-rose-500/10 px-3 py-2 text-sm text-rose-300" data-testid="admin-login-error">{error}</p>}
        <button type="submit" disabled={loading} data-testid="admin-login-submit" className={`mt-6 w-full ${btnGold}`}>
          {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Sign In"}
        </button>
      </form>
    </AuthLayout>
  );
}

/* ============================ Respond modal (AI draft) ============================ */
function ChannelPanel({ icon: Icon, heading, meta, children }) {
  return (
    <section className="flex flex-col rounded-xl border border-[var(--a-border)] bg-[var(--a-surface2)] p-4 sm:p-5">
      <div className="mb-3 flex items-center gap-2 text-[var(--a-gold-ink)]">
        <Icon className="h-4 w-4" strokeWidth={1.75} />
        <span className="text-sm font-semibold text-[var(--a-text)]">{heading}</span>
        {meta && <span className="truncate text-xs font-normal text-[var(--a-text-dim)]">· {meta}</span>}
      </div>
      {children}
    </section>
  );
}

function RespondModal({ token, submission, onClose, onSent }) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [draft, setDraft] = useState({ email_subject: "", email_body: "", sms_text: "", to_email: "", to_phone: "", ctas: [] });
  const [copied, setCopied] = useState("");
  const [sending, setSending] = useState("");
  const [result, setResult] = useState(null);
  useEffect(() => {
    draftResponse(token, submission.id)
      .then((d) => setDraft((prev) => ({ ...prev, ...d, ctas: d.ctas || [] })))
      .catch((e) => setError(e?.response?.data?.detail || "Could not generate a draft."))
      .finally(() => setLoading(false));
  }, [token, submission.id]);
  const copy = (key, text) => { navigator.clipboard?.writeText(text); setCopied(key); setTimeout(() => setCopied(""), 1500); };
  // Absolute-URL CTAs derived from the AI's chosen actions
  const ctas = (draft.ctas || []).map((c) => ({ label: c.label, url: `${window.location.origin}${c.path}` }));
  const removeCta = (i) => setDraft((d) => ({ ...d, ctas: (d.ctas || []).filter((_, idx) => idx !== i) }));
  const ctaLines = ctas.map((c) => `\n\n${c.label}: ${c.url}`).join("");
  // Quick Send passes CTAs separately (email -> real buttons, SMS -> labeled links).
  // The mailto/sms fallbacks can't render buttons, so include the link text there.
  const mailto = `mailto:${draft.to_email}?subject=${encodeURIComponent(draft.email_subject)}&body=${encodeURIComponent(draft.email_body + ctaLines)}`;
  const smsHref = draft.to_phone ? `sms:${draft.to_phone}?body=${encodeURIComponent(draft.sms_text + ctaLines)}` : "";

  const quickSend = async (channel) => {
    setSending(channel); setResult(null);
    const to = channel === "email" ? draft.to_email : draft.to_phone;
    const body = channel === "email" ? draft.email_body : draft.sms_text;
    try {
      const res = await sendResponse(token, submission.id, { channel, to, subject: draft.email_subject, body, ctas });
      if (res.sent) {
        setResult({ tone: "ok", msg: `Sent to ${to}. Submission marked Actioned.` });
        onSent?.(submission.id, { channel, to });
      } else {
        setResult({ tone: "info", msg: res.reason || "Not sent." });
      }
    } catch (e) {
      setResult({ tone: "error", msg: e?.response?.data?.detail || "Could not send. Please try again." });
    } finally { setSending(""); }
  };
  const resultTone = {
    ok: "border-emerald-500/20 bg-emerald-500/10 text-emerald-300",
    info: "border-sky-500/20 bg-sky-500/10 text-sky-200",
    error: "border-rose-500/20 bg-rose-500/10 text-rose-300",
  };

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center overflow-y-auto bg-black/70 p-4 backdrop-blur-sm" onClick={onClose} data-testid="respond-modal">
      <div className="my-auto flex max-h-[90vh] w-full max-w-3xl flex-col overflow-hidden rounded-2xl border border-[var(--a-border)] bg-[var(--a-surface)] shadow-2xl sm:max-h-[92vh] sm:w-[94vw] sm:max-w-[1160px]" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="flex shrink-0 items-start justify-between gap-3 border-b border-[var(--a-border)] bg-[var(--a-surface)] px-5 py-4 sm:px-6 sm:py-5">
          <div>
            <h3 className="flex items-center gap-2 font-heading text-lg font-semibold tracking-tight"><Sparkles className="h-5 w-5 text-[var(--a-gold-ink)]" strokeWidth={1.75} /> Smart-drafted response</h3>
            <p className="mt-1 text-xs leading-relaxed text-[var(--a-text-muted)]">This is a starting draft — <span className="font-semibold text-[var(--a-text2)]">edit anything below before you send</span>. Sends from thefrentfoundation@thefrent.com · 757-209-4786.</p>
          </div>
          <button onClick={onClose} data-testid="respond-close" className="grid h-9 w-9 shrink-0 place-items-center rounded-lg text-[var(--a-text-muted)] transition-colors hover:bg-[var(--a-hover)] hover:text-[var(--a-text)]"><X className="h-4 w-4" /></button>
        </div>

        {/* Body (scrolls) */}
        <div className="flex-1 overflow-y-auto px-5 py-5 sm:px-6 sm:py-6" style={{ paddingBottom: "max(1.5rem, env(safe-area-inset-bottom))" }}>
          {loading ? (
            <div className="grid place-items-center gap-3 py-20 text-[var(--a-text-muted)]"><Loader2 className="h-8 w-8 animate-spin text-[var(--a-gold-ink)]" /><span className="text-sm">Reading their message and drafting a thoughtful reply…</span></div>
          ) : error ? (
            <p className="rounded-lg border border-rose-500/20 bg-rose-500/10 px-3 py-2 text-sm text-rose-300">{error}</p>
          ) : (
            <div className="space-y-5">
              {result && (
                <p data-testid="respond-send-result" className={`rounded-lg border px-3 py-2.5 text-sm ${resultTone[result.tone] || resultTone.info}`}>{result.msg}</p>
              )}

              <div className="grid gap-6 lg:grid-cols-2 lg:items-start lg:gap-8">
                {/* Email */}
                <ChannelPanel icon={Mail} heading="Email" meta={draft.to_email || "no email on file"}>
                  <label className={labelCls}>Subject</label>
                  <Input data-testid="respond-email-subject" value={draft.email_subject} onChange={(e) => setDraft({ ...draft, email_subject: e.target.value })} className={`mt-1.5 mb-3 h-11 rounded-lg border-[var(--a-border)] bg-[var(--a-bg)] text-base text-[var(--a-text)]`} />
                  <label className={labelCls}>Body</label>
                  <textarea data-testid="respond-email-body" value={draft.email_body} onChange={(e) => setDraft({ ...draft, email_body: e.target.value })} rows={9} className="mt-1.5 w-full rounded-lg border border-[var(--a-border)] bg-[var(--a-bg)] p-3 text-sm leading-relaxed text-[var(--a-text)] focus:border-[#D9A93E] focus:outline-none focus:ring-2 focus:ring-[#D9A93E]/40" />
                  {/* Live preview of the branded email exactly as the recipient sees it */}
                  <div className="mt-3 mx-auto w-full max-w-[500px] overflow-hidden rounded-2xl border border-[#C79A3E]/30 bg-[#121D29] shadow-lg" data-testid="respond-email-preview">
                    {/* Header (3x high-res image) */}
                    <img src={EMAIL_HEADER_IMG} alt="Thefrent Foundation — Educate. Empower. Elevate." className="block w-full" />
                    {/* Body (navy) */}
                    <div className="bg-[#121D29] px-6 py-6">
                      {draft.email_body.split("\n").map((l) => l.trim()).filter(Boolean).map((line, i) => {
                        const m = line.match(/^[-*•–·]\s+(.*)$/);
                        if (m) {
                          return (
                            <div key={i} className="mb-2.5 flex items-start gap-2.5">
                              <span className="mt-[7px] h-[7px] w-[7px] shrink-0 rounded-full bg-[#E0B25A]" />
                              <span className="text-sm leading-[1.7] text-[#E8ECF4]">{m[1]}</span>
                            </div>
                          );
                        }
                        return <p key={i} className="mb-4 text-sm leading-[1.7] text-[#E8ECF4]">{line}</p>;
                      })}
                      {ctas.length > 0 && (
                        <div className="mt-4 flex flex-col items-start gap-2.5">
                          {ctas.map((c, i) => (
                            <a key={`${c.label}-${i}`} href={c.url} target="_blank" rel="noreferrer" className="block w-56 rounded-full bg-[#D9A93E] px-6 py-2.5 text-center text-sm font-bold text-[#0F1F3D] no-underline transition-colors hover:bg-[#c8892b]">{c.label}</a>
                          ))}
                        </div>
                      )}
                    </div>
                    {/* Footer (3x high-res image) */}
                    <img src={EMAIL_FOOTER_IMG} alt="Contact Us — thefrentfoundation@thefrent.com · 757-209-4786 · Making an impact in communities everywhere." className="block w-full" />
                  </div>
                  {ctas.length > 0 && (
                    <div className="mt-3 rounded-lg border border-[var(--a-border)] bg-[var(--a-surface2)] p-3" data-testid="respond-email-links">
                      <Collapse label="Links added" count={ctas.length} defaultOpen testId="respond-email-links-collapse" headerClassName="text-[var(--a-gold-ink)]">
                        {ctas.map((c, i) => (
                          <div key={`${c.label}-${i}`} className="flex items-center justify-between gap-2 py-0.5">
                            <p className="min-w-0 truncate text-xs text-[var(--a-gold-ink)]">{c.label}: <span className="text-[var(--a-text-muted)]">{c.url}</span></p>
                            <button type="button" onClick={() => removeCta(i)} data-testid={`respond-cta-remove-${i}`} aria-label={`Remove ${c.label} button`} className="grid h-6 w-6 shrink-0 place-items-center rounded-full text-[var(--a-text-muted)] transition-colors hover:bg-rose-500/15 hover:text-rose-400"><X className="h-3.5 w-3.5" /></button>
                          </div>
                        ))}
                      </Collapse>
                    </div>
                  )}
                  <div className="mt-3 flex flex-wrap gap-2">
                    {draft.to_email && (
                      <button onClick={() => quickSend("email")} disabled={sending === "email"} data-testid="respond-quicksend-email" className={btnGold}>
                        {sending === "email" ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />} Send now
                      </button>
                    )}
                    <a href={mailto} data-testid="respond-send-email" className={btnChip}><Mail className="h-4 w-4" /> Open in email</a>
                    <button onClick={() => copy("email", `${draft.email_subject}\n\n${draft.email_body}${ctaLines}`)} className={btnChip}><Copy className="h-4 w-4" /> {copied === "email" ? "Copied!" : "Copy"}</button>
                  </div>
                </ChannelPanel>

                {/* SMS */}
                <ChannelPanel icon={MessageSquare} heading="Text message" meta={draft.to_phone || "no phone on file"}>
                  <label className={labelCls}>Message</label>
                  <textarea data-testid="respond-sms" value={draft.sms_text} onChange={(e) => setDraft({ ...draft, sms_text: e.target.value })} rows={6} className="mt-1.5 w-full rounded-lg border border-[var(--a-border)] bg-[var(--a-bg)] p-3 text-sm leading-relaxed text-[var(--a-text)] focus:border-[#D9A93E] focus:outline-none focus:ring-2 focus:ring-[#D9A93E]/40" />
                  {ctas.length > 0 && (
                    <div className="mt-3 rounded-lg border border-[var(--a-border)] bg-[var(--a-surface2)] p-3" data-testid="respond-sms-preview">
                      <Collapse label="Links added to the text" count={ctas.length} defaultOpen testId="respond-sms-links-collapse" headerClassName="text-[var(--a-text-muted)]">
                        {ctas.map((c, i) => (
                          <div key={`${c.label}-${i}`} className="flex items-center justify-between gap-2 py-0.5">
                            <p className="min-w-0 truncate text-xs text-sky-300">{c.label}: <span className="text-[var(--a-text-muted)]">{c.url}</span></p>
                            <button type="button" onClick={() => removeCta(i)} data-testid={`respond-sms-cta-remove-${i}`} aria-label={`Remove ${c.label} link`} className="grid h-6 w-6 shrink-0 place-items-center rounded-full text-[var(--a-text-muted)] transition-colors hover:bg-rose-500/15 hover:text-rose-400"><X className="h-3.5 w-3.5" /></button>
                          </div>
                        ))}
                      </Collapse>
                    </div>
                  )}
                  <div className="mt-3 flex flex-wrap gap-2">
                    {draft.to_phone && (
                      <button onClick={() => quickSend("sms")} disabled={sending === "sms"} data-testid="respond-quicksend-sms" className={btnGold}>
                        {sending === "sms" ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />} Send now
                      </button>
                    )}
                    {draft.to_phone && <a href={smsHref} data-testid="respond-send-sms" className={btnChip}><MessageSquare className="h-4 w-4" /> Open in messages</a>}
                    <button onClick={() => copy("sms", `${draft.sms_text}${ctaLines}`)} className={btnChip}><Copy className="h-4 w-4" /> {copied === "sms" ? "Copied!" : "Copy"}</button>
                  </div>
                </ChannelPanel>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

/* ============================ Status control ============================ */
function StatusPill({ status, onClick, testId }) {
  const m = STATUS_META[status] || STATUS_META.new;
  return (
    <button type="button" onClick={onClick} data-testid={testId} title="View status history"
      className={`inline-flex items-center gap-1.5 rounded-md px-2 py-0.5 text-[0.65rem] font-semibold uppercase tracking-wide ring-1 transition-transform hover:scale-[1.04] ${m.cls}`}>
      <span className={`h-1.5 w-1.5 rounded-full ${m.dot}`} />{m.label}
    </button>
  );
}

function Overlay({ onClose, children, testId, maxW = "modal-w-lg" }) {
  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose?.(); };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);
  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center overflow-y-auto bg-black/70 p-4 backdrop-blur-sm" onClick={onClose} data-testid={testId}>
      <div className={`my-auto flex max-h-[90vh] w-full ${maxW} flex-col overflow-hidden rounded-2xl border border-[var(--a-border)] bg-[var(--a-surface)] shadow-2xl`} onClick={(e) => e.stopPropagation()}>
        {children}
      </div>
    </div>
  );
}

function ModalHeader({ icon: Icon, title, subtitle, onClose, testId }) {
  return (
    <div className="flex shrink-0 items-start justify-between gap-3 border-b border-[var(--a-border)] px-5 py-4">
      <div>
        <h3 className="flex items-center gap-2 font-heading text-lg font-semibold tracking-tight text-[var(--a-text)]"><Icon className="h-5 w-5 text-[var(--a-gold-ink)]" strokeWidth={1.75} /> {title}</h3>
        {subtitle && <p className="mt-1 text-xs leading-relaxed text-[var(--a-text-muted)]">{subtitle}</p>}
      </div>
      <button onClick={onClose} data-testid={testId} className="grid h-9 w-9 shrink-0 place-items-center rounded-lg text-[var(--a-text-muted)] transition-colors hover:bg-[var(--a-hover)] hover:text-[var(--a-text)]"><X className="h-4 w-4" /></button>
    </div>
  );
}

function HistoryModal({ submission, onClose }) {
  // The timeline must always start from the "Submission received" (new) event. Older
  // records may have no history, or a partial one that starts at "reviewed"; in both cases
  // derive the received entry from created_at and prepend it. The backend also normalises
  // this on startup, so this is a display-time safety net.
  const rawHist = submission.history || [];
  const hasReceived = rawHist.some((h) => h.status === "new" || h.summary === "Submission received");
  const withReceived = hasReceived ? rawHist : [
    { id: "received", at: submission.created_at, status: "new", actor_type: "system", actor: "System", summary: "Submission received", actions: [], note: "" },
    ...rawHist,
  ];
  const entries = [...withReceived].reverse();
  return (
    <Overlay onClose={onClose} testId="history-modal" maxW="modal-w-lg">
      <ModalHeader icon={History} title="Status history" subtitle={`${TYPE_LABEL[submission.type] || submission.type} · ${displayName(submission)}`} onClose={onClose} testId="history-close" />
      <div className="flex-1 overflow-y-auto px-5 py-5">
        {entries.length === 0 ? (
          <p className="text-sm text-[var(--a-text-muted)]">No history recorded yet.</p>
        ) : (
          <ol className="relative space-y-4 border-l border-[var(--a-border)] pl-5" data-testid="history-timeline">
            {entries.map((h) => {
              const m = h.status ? STATUS_META[h.status] : null;
              return (
                <li key={h.id} className="relative" data-testid="history-entry">
                  <span className={`absolute -left-[1.42rem] top-1 h-2.5 w-2.5 rounded-full ring-2 ring-[var(--a-surface)] ${m ? m.dot : "bg-[var(--a-gold-ink)]"}`} />
                  <div className="flex flex-wrap items-center gap-2">
                    {m && <span className={`rounded px-1.5 py-0.5 text-[0.6rem] font-semibold uppercase tracking-wide ring-1 ${m.cls}`}>{m.label}</span>}
                    <span className="text-sm font-medium text-[var(--a-text)]">{h.summary}</span>
                  </div>
                  <p className="mt-0.5 flex items-center gap-1.5 text-xs text-[var(--a-text-dim)]"><Clock className="h-3 w-3" /> {fmtDate(h.at)} · {h.actor_type === "system" ? "System" : h.actor}</p>
                  {h.actions?.length > 0 && (
                    <ul className="mt-1.5 flex flex-wrap gap-1.5">
                      {h.actions.map((a, i) => <li key={i} className="rounded-md bg-[var(--a-hover)] px-2 py-0.5 text-xs text-[var(--a-text2)]">{a}</li>)}
                    </ul>
                  )}
                  {h.note && <p className="mt-1.5 rounded-lg border border-[var(--a-border)] bg-[var(--a-surface2)] px-3 py-2 text-sm text-[var(--a-text2)] [overflow-wrap:anywhere]">{h.note}</p>}
                </li>
              );
            })}
          </ol>
        )}
      </div>
    </Overlay>
  );
}

function LogActionModal({ token, submission, onClose, onDone }) {
  const isActioned = submission.status === "actioned";
  const [target, setTarget] = useState("actioned");
  const options = ACTIONS_BY_CATEGORY[submission.type] || [];
  const [selected, setSelected] = useState([]);
  const [otherOn, setOtherOn] = useState(false);
  const [otherText, setOtherText] = useState("");
  const [note, setNote] = useState("");
  const [followUp, setFollowUp] = useState(submission.follow_up || "");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const toggle = (label) => setSelected((s) => (s.includes(label) ? s.filter((x) => x !== label) : [...s, label]));
  const actions = [...selected, ...(otherOn && otherText.trim() ? [otherText.trim()] : [])];
  const submit = async () => {
    if (target === "actioned" && actions.length === 0 && !note.trim()) { setError("Select at least one action or add a note."); return; }
    setBusy(true); setError("");
    try {
      await logSubmissionAction(token, submission.id, { target_status: target, actions: actions.map((label) => ({ label })), note: note.trim(), follow_up: followUp || null });
      onDone();
    } catch (e) { setError(errMsg(e, "Could not log this action.")); setBusy(false); }
  };
  return (
    <Overlay onClose={onClose} testId="log-action-modal" maxW="modal-w-lg">
      <ModalHeader icon={ListPlus} title={isActioned ? "Log additional action" : "Log action"} subtitle={`${TYPE_LABEL[submission.type] || submission.type} · ${displayName(submission)}`} onClose={onClose} testId="log-action-close" />
      <div className="flex-1 overflow-y-auto px-5 py-5">
        {!isActioned && (
          <div className="mb-4">
            <label className={labelCls}>Outcome</label>
            <div className="mt-2 grid grid-cols-2 gap-2">
              <button type="button" onClick={() => setTarget("reviewed")} data-testid="log-target-reviewed" className={`rounded-lg border px-3 py-2.5 text-sm font-medium transition-colors ${target === "reviewed" ? "border-[#D9A93E] bg-[#D9A93E]/10 text-[var(--a-gold-ink)]" : "border-[var(--a-border)] text-[var(--a-text2)] hover:border-[var(--a-border2)]"}`}>Mark reviewed</button>
              <button type="button" onClick={() => setTarget("actioned")} data-testid="log-target-actioned" className={`rounded-lg border px-3 py-2.5 text-sm font-medium transition-colors ${target === "actioned" ? "border-[#D9A93E] bg-[#D9A93E]/10 text-[var(--a-gold-ink)]" : "border-[var(--a-border)] text-[var(--a-text2)] hover:border-[var(--a-border2)]"}`}>Reviewed &amp; actioned</button>
            </div>
          </div>
        )}
        <label className={labelCls}>What was done <span className="normal-case text-[var(--a-text-dim)]">(select all that apply)</span></label>
        <div className="mt-2 grid gap-1.5 sm:grid-cols-2" data-testid="log-action-options">
          {options.map((label) => (
            <label key={label} className={`flex cursor-pointer items-center gap-2 rounded-lg border px-3 py-2 text-sm transition-colors ${selected.includes(label) ? "border-[#D9A93E]/60 bg-[#D9A93E]/8 text-[var(--a-text)]" : "border-[var(--a-border)] text-[var(--a-text2)] hover:border-[var(--a-border2)]"}`}>
              <input type="checkbox" checked={selected.includes(label)} onChange={() => toggle(label)} className="h-4 w-4 rounded border-[var(--a-border)] accent-[#D9A93E]" />
              {label}
            </label>
          ))}
          <label className={`flex cursor-pointer items-center gap-2 rounded-lg border px-3 py-2 text-sm transition-colors ${otherOn ? "border-[#D9A93E]/60 bg-[#D9A93E]/8 text-[var(--a-text)]" : "border-[var(--a-border)] text-[var(--a-text2)] hover:border-[var(--a-border2)]"}`}>
            <input type="checkbox" checked={otherOn} onChange={(e) => setOtherOn(e.target.checked)} className="h-4 w-4 rounded border-[var(--a-border)] accent-[#D9A93E]" data-testid="log-other-check" />
            Other
          </label>
        </div>
        {otherOn && (
          <input value={otherText} onChange={(e) => setOtherText(e.target.value)} placeholder="Describe the action taken…" data-testid="log-other-text"
            className="mt-2 h-10 w-full rounded-lg border border-[var(--a-border)] bg-[var(--a-field)] px-3 text-sm text-[var(--a-text)] shadow-inner focus:border-[#D9A93E] focus:outline-none focus:ring-2 focus:ring-[#D9A93E]/40" />
        )}
        <label className={`${labelCls} mt-4`}>Note <span className="normal-case text-[var(--a-text-dim)]">(optional)</span></label>
        <textarea value={note} onChange={(e) => setNote(e.target.value)} rows={3} data-testid="log-note"
          placeholder="Any extra context for the record…"
          className="mt-2 w-full rounded-lg border border-[var(--a-border)] bg-[var(--a-field)] px-3 py-2 text-sm text-[var(--a-text)] shadow-inner focus:border-[#D9A93E] focus:outline-none focus:ring-2 focus:ring-[#D9A93E]/40" />
        <label className={`${labelCls} mt-4`}>Follow-up date <span className="normal-case text-[var(--a-text-dim)]">(optional — the portal will flag it when due)</span></label>
        <div className="mt-2 flex items-center gap-2">
          <input type="date" value={followUp} onChange={(e) => setFollowUp(e.target.value)} data-testid="log-followup"
            className="h-10 rounded-lg border border-[var(--a-border)] bg-[var(--a-field)] px-3 text-sm text-[var(--a-text)] shadow-inner focus:border-[#D9A93E] focus:outline-none focus:ring-2 focus:ring-[#D9A93E]/40 [color-scheme:light]" />
          {followUp && <button type="button" onClick={() => setFollowUp("")} data-testid="log-followup-clear" className="text-xs font-medium text-[var(--a-text-dim)] hover:text-[var(--a-gold-ink)]">Clear</button>}
        </div>
        {error && <p className="mt-3 rounded-lg border border-rose-400/30 bg-rose-400/10 px-3 py-2 text-sm text-rose-300">{error}</p>}
      </div>
      <div className="flex shrink-0 justify-end gap-2 border-t border-[var(--a-border)] px-5 py-4">
        <button onClick={onClose} className={btnSecondary}>Cancel</button>
        <button onClick={submit} disabled={busy} data-testid="log-action-submit" className={btnGold}>{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Check className="h-4 w-4" /> Save to record</>}</button>
      </div>
    </Overlay>
  );
}

function CloseModal({ token, submission, onClose, onDone }) {
  const nowLocal = () => { const d = new Date(); const p = (n) => String(n).padStart(2, "0"); return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}T${p(d.getHours())}:${p(d.getMinutes())}`; };
  const [when, setWhen] = useState(nowLocal());
  const [reason, setReason] = useState("");
  const [remarks, setRemarks] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const reasons = CLOSE_REASONS[submission.type] || DEFAULT_CLOSE_REASONS;
  const submit = async () => {
    if (!reason) { setError("Please choose a reason for closing."); return; }
    if (reason === "Other" && !remarks.trim()) { setError("Please describe the reason in the closing remarks."); return; }
    setBusy(true); setError("");
    try {
      const at = when ? new Date(when).toISOString() : undefined;
      const finalReason = reason === "Other" ? remarks.trim() : reason;
      await closeSubmission(token, submission.id, { reason: finalReason, remarks: remarks.trim(), at });
      onDone();
    } catch (e) { setError(errMsg(e, "Could not close this submission.")); setBusy(false); }
  };
  return (
    <Overlay onClose={onClose} testId="close-modal" maxW="modal-w-md">
      <ModalHeader icon={CheckCircle2} title="Close submission" subtitle="This finalises the record. Only a master admin can reopen it afterwards." onClose={onClose} testId="close-modal-close" />
      <div className="flex-1 overflow-y-auto px-5 py-5">
        <label className={labelCls}>Reason for closing</label>
        <div className="mt-2 grid gap-1.5 sm:grid-cols-2" data-testid="close-reason-options">
          {reasons.map((r) => (
            <button type="button" key={r} onClick={() => setReason(r)} data-testid={`close-reason-${r.toLowerCase().replace(/[^a-z]+/g, "-").replace(/^-|-$/g, "")}`}
              className={`flex items-center gap-2 rounded-lg border px-3 py-2.5 text-left text-sm transition-colors ${reason === r ? "border-[#D9A93E] bg-[#D9A93E]/10 text-[var(--a-text)]" : "border-[var(--a-border)] bg-[var(--a-surface2)] text-[var(--a-text2)] hover:border-[var(--a-border2)]"}`}>
              <span className={`grid h-4 w-4 shrink-0 place-items-center rounded-full border ${reason === r ? "border-[#D9A93E]" : "border-[var(--a-border2)]"}`}>{reason === r && <span className="h-2 w-2 rounded-full bg-[#D9A93E]" />}</span>
              {r}
            </button>
          ))}
        </div>
        <label className={`${labelCls} mt-4`}>Date &amp; time closed</label>
        <input type="datetime-local" value={when} onChange={(e) => setWhen(e.target.value)} data-testid="close-datetime"
          className="mt-2 h-10 w-full rounded-lg border border-[var(--a-border)] bg-[var(--a-field)] px-3 text-sm text-[var(--a-text)] shadow-inner focus:border-[#D9A93E] focus:outline-none focus:ring-2 focus:ring-[#D9A93E]/40" />
        <label className={`${labelCls} mt-4`}>{reason === "Other" ? "Closing remarks (required)" : "Closing remarks (optional)"}</label>
        <textarea value={remarks} onChange={(e) => setRemarks(e.target.value)} rows={4} data-testid="close-remarks"
          placeholder={REMARKS_PLACEHOLDER[submission.type] || "Summarise how this was resolved and any final outcome…"}
          className="mt-2 w-full rounded-lg border border-[var(--a-border)] bg-[var(--a-field)] px-3 py-2 text-sm text-[var(--a-text)] shadow-inner focus:border-[#D9A93E] focus:outline-none focus:ring-2 focus:ring-[#D9A93E]/40" />
        {error && <p className="mt-3 rounded-lg border border-rose-400/30 bg-rose-400/10 px-3 py-2 text-sm text-rose-300" data-testid="close-error">{error}</p>}
      </div>
      <div className="flex shrink-0 justify-end gap-2 border-t border-[var(--a-border)] px-5 py-4">
        <button onClick={onClose} className={btnSecondary}>Cancel</button>
        <button onClick={submit} disabled={busy} data-testid="close-submit" className={btnGold}>{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Lock className="h-4 w-4" /> Close out</>}</button>
      </div>
    </Overlay>
  );
}

const ARCHIVE_REASONS = ["Resolved / completed", "Duplicate submission", "Spam or not genuine", "No longer relevant", "Test entry", "Other"];

function ArchiveSubmissionModal({ token, submission, onClose, onDone }) {
  const [reason, setReason] = useState("");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const submit = async () => {
    if (!reason) { setError("Please choose a reason for archiving."); return; }
    if (reason === "Other" && !note.trim()) { setError("Please describe the reason in the note."); return; }
    setBusy(true); setError("");
    try {
      const finalReason = reason === "Other" ? note.trim() : reason;
      await archiveSubmission(token, submission.id, { reason: finalReason, note: note.trim() });
      onDone();
    } catch (e) { setError(errMsg(e, "Could not archive this submission.")); setBusy(false); }
  };
  return (
    <Overlay onClose={onClose} testId="archive-submission-modal" maxW="modal-w-md">
      <ModalHeader icon={Archive} title="Archive submission" subtitle="Give a reason so the archive stays auditable — you can restore it later." onClose={onClose} testId="archive-submission-close" />
      <div className="flex-1 overflow-y-auto px-5 py-5">
        <label className={labelCls}>Reason for archiving</label>
        <div className="mt-2 grid gap-1.5 sm:grid-cols-2" data-testid="archive-reason-options">
          {ARCHIVE_REASONS.map((r) => (
            <button type="button" key={r} onClick={() => setReason(r)} data-testid={`archive-reason-${r.toLowerCase().replace(/[^a-z]+/g, "-").replace(/^-|-$/g, "")}`}
              className={`flex items-center gap-2 rounded-lg border px-3 py-2.5 text-left text-sm transition-colors ${reason === r ? "border-[#D9A93E] bg-[#D9A93E]/10 text-[var(--a-text)]" : "border-[var(--a-border)] bg-[var(--a-surface2)] text-[var(--a-text2)] hover:border-[var(--a-border2)]"}`}>
              <span className={`grid h-4 w-4 shrink-0 place-items-center rounded-full border ${reason === r ? "border-[#D9A93E]" : "border-[var(--a-border2)]"}`}>{reason === r && <span className="h-2 w-2 rounded-full bg-[#D9A93E]" />}</span>
              {r}
            </button>
          ))}
        </div>
        <label className={`${labelCls} mt-4`}>{reason === "Other" ? "Explain (required)" : "Note (optional)"}</label>
        <textarea value={note} onChange={(e) => setNote(e.target.value)} rows={3} data-testid="archive-note"
          placeholder={reason === "Other" ? "Describe why this is being archived…" : "Add any extra context for the record…"}
          className="mt-2 w-full rounded-lg border border-[var(--a-border)] bg-[var(--a-field)] px-3 py-2 text-sm text-[var(--a-text)] shadow-inner focus:border-[#D9A93E] focus:outline-none focus:ring-2 focus:ring-[#D9A93E]/40" />
        {error && <p className="mt-3 rounded-lg border border-rose-400/30 bg-rose-400/10 px-3 py-2 text-sm text-rose-300" data-testid="archive-error">{error}</p>}
      </div>
      <div className="flex shrink-0 justify-end gap-2 border-t border-[var(--a-border)] px-5 py-4">
        <button onClick={onClose} className={btnSecondary}>Cancel</button>
        <button onClick={submit} disabled={busy} data-testid="archive-submit" className={btnGold}>{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Archive className="h-4 w-4" /> Archive</>}</button>
      </div>
    </Overlay>
  );
}

/* ============================ Submissions ============================ */
function renderBodyLines(body) {
  return (body || "").split("\n").map((l) => l.trim()).filter(Boolean).map((line, i) => {
    const m = line.match(/^[-*•–·]\s+(.*)$/);
    if (m) return (
      <div key={i} className="mb-2 flex items-start gap-2">
        <span className="mt-[6px] h-[6px] w-[6px] shrink-0 rounded-full bg-[#E0B25A]" />
        <span className="text-[13px] leading-[1.7] text-[#E8ECF4]">{m[1]}</span>
      </div>
    );
    return <p key={i} className="mb-3 text-[13px] leading-[1.7] text-[#E8ECF4]">{line}</p>;
  });
}

function EmailRender({ body, ctas }) {
  return (
    <div className="overflow-hidden rounded-xl border border-[#C79A3E]/30 bg-[#121D29]">
      <img src={EMAIL_HEADER_IMG} alt="Thefrent Foundation" className="block w-full" />
      <div className="bg-[#121D29] px-5 py-5">
        {renderBodyLines(body)}
        {ctas?.length > 0 && (
          <div className="mt-3 flex flex-col items-start gap-2">
            {ctas.map((c, i) => (
              <span key={i} className="rounded-full bg-[#D9A93E] px-5 py-2 text-[13px] font-bold text-[#0F1F3D]">{c.label}</span>
            ))}
          </div>
        )}
      </div>
      <img src={EMAIL_FOOTER_IMG} alt="Contact Us" className="block w-full" />
    </div>
  );
}

function ResponseItem({ token, sid, r, onChange }) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const archived = !!r.archived;
  const toggleArchive = async (e) => {
    e?.stopPropagation?.();
    if (!r.id) return;
    setBusy(true);
    try { await archiveResponse(token, sid, r.id, !archived); onChange?.(); } catch { /* noop */ } finally { setBusy(false); }
  };
  return (
    <li className={`rounded-lg border border-[var(--a-border)] bg-[var(--a-surface2)] ${archived ? "opacity-60" : ""}`}>
      <div className="flex items-center gap-2 px-3 py-2">
        <button onClick={() => setOpen((o) => !o)} data-testid={`response-toggle-${r.id || ""}`} className="flex min-w-0 flex-1 items-center gap-2 text-left text-xs text-[var(--a-text-muted)] transition-colors hover:text-[var(--a-text2)]">
          <ChevronDown className={`h-3.5 w-3.5 shrink-0 text-[var(--a-text-dim)] transition-transform ${open ? "" : "-rotate-90"}`} />
          <span className="truncate">
            <span className="font-semibold text-[var(--a-text2)]">{r.channel === "email" ? "Email" : "Text"}</span> to {r.to}
            <span className="text-[var(--a-text-dim)]"> · {fmtDate(r.sent_at)}</span>
            {archived && <span className="ml-1.5 rounded bg-slate-600/40 px-1.5 py-0.5 text-[0.6rem] uppercase tracking-wide text-[var(--a-text2)]">Archived</span>}
          </span>
        </button>
        {r.id && (
          <button onClick={toggleArchive} disabled={busy} data-testid={`response-archive-${r.id}`} title={archived ? "Unarchive" : "Archive"} className="grid h-7 w-7 shrink-0 place-items-center rounded-md text-[var(--a-text-dim)] transition-colors hover:bg-[var(--a-hover)] hover:text-[var(--a-text2)]">
            {archived ? <ArchiveRestore className="h-3.5 w-3.5" /> : <Archive className="h-3.5 w-3.5" />}
          </button>
        )}
      </div>
      {open && (
        <div className="border-t border-[var(--a-border)] px-3 py-3" data-testid={`response-detail-${r.id || ""}`}>
          {r.channel === "email" && r.subject && <p className="mb-2 text-xs text-[var(--a-text-muted)]"><span className="font-semibold text-[var(--a-text2)]">Subject:</span> {r.subject}</p>}
          {r.channel === "email" && r.body != null
            ? <EmailRender body={r.body} ctas={r.ctas} />
            : r.body != null
              ? <div className="rounded-lg bg-[#121D29] px-4 py-4">{renderBodyLines(r.body)}</div>
              : <p className="text-xs italic text-[var(--a-text-dim)]">Full content isn&apos;t stored for this older response.{r.buttons?.length ? ` Buttons: ${r.buttons.join(", ")}` : ""}</p>}
          {r.sent_by && <p className="mt-2 text-[0.65rem] text-[var(--a-text-dim)]">Sent by {r.sent_by}</p>}
        </div>
      )}
    </li>
  );
}

function SentPopup({ info, onClose }) {
  if (!info) return null;
  return (
    <div className="fixed inset-0 z-[110] flex items-center justify-center bg-black/70 p-4 backdrop-blur-sm" onClick={onClose} data-testid="sent-popup">
      <div className="w-full modal-w-sm rounded-2xl border border-[#C79A3E]/30 bg-[var(--a-surface)] p-7 text-center shadow-2xl" onClick={(e) => e.stopPropagation()}>
        <div className="mx-auto grid h-16 w-16 place-items-center rounded-full bg-emerald-500/15">
          <CheckCircle2 className="h-9 w-9 text-emerald-400" strokeWidth={1.75} />
        </div>
        <h3 className="mt-4 font-heading text-lg font-semibold text-[var(--a-text)]">{info.channel === "email" ? "Email sent" : "Text sent"}</h3>
        <p className="mt-1.5 text-sm leading-relaxed text-[var(--a-text2)]">Your {info.channel === "email" ? "email" : "message"} to <span className="font-semibold text-[var(--a-gold-ink)]">{info.to}</span> was delivered and saved to the follow-up history.</p>
        <button onClick={onClose} data-testid="sent-popup-done" className="mt-5 w-full rounded-full bg-[#D9A93E] px-6 py-2.5 text-sm font-bold text-[#0F1F3D] transition-colors hover:bg-[#c8892b]">Done</button>
      </div>
    </div>
  );
}

const cardEntries = (s) => Object.entries(s).filter(([k, v]) => !HIDDEN.has(k) && k !== "created_at" && k !== "status" && v !== "" && v != null);

// Shared search matcher used across every portal filter. Case-insensitive substring match
// that ALSO matches when whitespace/hyphens are stripped from both sides — so a concatenated
// query like "shawnakmcpherson" still finds "Shawna-Kay McPherson". Only the user-facing text
// passed in is searched (never hidden IDs/timestamps), which prevents the false positives that
// a blanket JSON.stringify match produced.
const searchMatch = (query, ...fields) => {
  const q = (query || "").trim().toLowerCase();
  if (!q) return true;
  const blob = fields.map((v) => (Array.isArray(v) ? v.join(" ") : v == null ? "" : String(v))).join(" ").toLowerCase();
  return blob.includes(q) || blob.replace(/[\s-]+/g, "").includes(q.replace(/[\s-]+/g, ""));
};

// All user-facing text on a submission: visible fields + follow-up (responses) + status history,
// so admins can search by anything they can actually read on/behind the card. We intentionally
// exclude the acting admin's identity (response.sent_by / history.actor) because that is the
// person who HANDLED the submission, not its content — including it made a name search match
// every item that admin ever touched.
const submissionSearchText = (s) => {
  const parts = [displayName(s)];
  for (const [, v] of cardEntries(s)) parts.push(Array.isArray(v) ? v.join(" ") : String(v));
  (s.responses || []).forEach((r) => parts.push(r.subject, r.body, r.to, (r.buttons || []).join(" "), (r.ctas || []).map((c) => c.label).join(" ")));
  (s.history || []).forEach((h) => parts.push(h.summary, h.note, (h.actions || []).map((a) => a.label || a).join(" ")));
  return parts.filter(Boolean).join(" ");
};

// Field list for a submission card. On mobile it collapses behind a "View details" chevron so a
// data-dense card only shows its essentials (badges, name, time, primary action) by default; on
// desktop (lg) it is always expanded. Single DOM node with a CSS grid-rows height animation — no
// duplicated markup, so test IDs stay unique.
function CardFields({ s }) {
  const [open, setOpen] = useState(false);
  const fields = cardEntries(s);
  const id = `card-fields-${s.id}`;
  if (fields.length === 0) return null;
  return (
    <div className="mt-4 flex-1 border-t border-[var(--a-border)] pt-3">
      <button type="button" onClick={() => setOpen((o) => !o)} aria-expanded={open} aria-controls={id}
        data-testid={`card-toggle-${s.id}`}
        className="flex w-full items-center gap-2 text-sm font-semibold text-[var(--a-text2)] transition-colors hover:text-[var(--a-gold-ink)] lg:hidden">
        <span>{open ? "Hide details" : "View details"}</span>
        <span className="grid h-5 min-w-[1.375rem] place-items-center rounded-full bg-[var(--a-chip)] px-1.5 text-xs font-medium tabular-nums text-[var(--a-text-muted)]">{fields.length}</span>
        <ChevronDown className={`ml-auto h-4 w-4 text-[var(--a-text-dim)] transition-transform duration-300 ${open ? "rotate-180" : ""}`} />
      </button>
      <div id={id} className={`grid transition-[grid-template-rows] duration-300 ease-[cubic-bezier(0.16,1,0.3,1)] lg:grid-rows-[1fr] ${open ? "grid-rows-[1fr]" : "grid-rows-[0fr]"}`}>
        <div className="overflow-hidden">
          <dl className="grid gap-x-5 gap-y-3 pt-3 text-sm lg:pt-1">
            {fields.map(([k, v]) => (
              <div key={k} className="flex min-w-0 flex-col">
                <dt className="text-[0.65rem] font-semibold uppercase tracking-[0.14em] text-[var(--a-text-dim)]">{k.replace(/_/g, " ")}</dt>
                <dd className="mt-0.5 break-words [overflow-wrap:anywhere] text-[var(--a-text2)]">{Array.isArray(v) ? v.join(", ") : String(v)}</dd>
              </div>
            ))}
          </dl>
        </div>
      </div>
    </div>
  );
}

// Follow-up history opens as a centered, adequately-sized popup (never inline) so expanding one
// card's history never reflows the neighbouring cards in the grid. `submission` is looked up
// fresh from live data by the parent, so archiving a response updates this list in place.
function FollowupHistoryModal({ token, submission, onClose, onChange }) {
  const responses = submission.responses || [];
  const activeCount = responses.filter((r) => !r.archived).length;
  return (
    <Overlay onClose={onClose} testId="followups-modal" maxW="modal-w-2xl">
      <ModalHeader icon={Send} title="Follow-up history"
        subtitle={`${TYPE_LABEL[submission.type] || submission.type} · ${displayName(submission)} · ${activeCount} sent`}
        onClose={onClose} testId="followups-modal-close" />
      <div className="flex-1 overflow-y-auto px-5 py-5">
        {responses.length === 0 ? (
          <p className="text-sm text-[var(--a-text-muted)]">No follow-ups recorded yet.</p>
        ) : (
          <ul className="space-y-2" data-testid="followups-modal-list">
            {responses.map((r, i) => (
              <ResponseItem key={r.id || i} token={token} sid={submission.id} r={r} onChange={onChange} />
            ))}
          </ul>
        )}
      </div>
    </Overlay>
  );
}




function SubmissionsView({ token, me, attention = false }) {
  const canReports = me?.role === "master" || (me?.permissions || []).includes("reports_view");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("all");
  const [q, setQ] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [responding, setResponding] = useState(null);
  const [sentInfo, setSentInfo] = useState(null);
  const [historyFor, setHistoryFor] = useState(null);
  const [followupsForId, setFollowupsForId] = useState(null);
  const [logFor, setLogFor] = useState(null);
  const [closeFor, setCloseFor] = useState(null);
  const [archiveFor, setArchiveFor] = useState(null);
  const [hideClosed, setHideClosed] = useState(false);
  const [showArchived, setShowArchived] = useState(false);
  const load = useCallback(() => getSubmissions(token).then(setData).catch(() => {}).finally(() => setLoading(false)), [token]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { setStatusFilter("all"); setTab("all"); }, [attention]);
  const allowed = data?.allowed || [];
  // Show an aggregate "All" tab whenever the admin can access more than one category.
  // The backend already scopes submissions/counts to `allowed`, so "All" never leaks
  // categories the admin isn't permitted to see.
  const showAllTab = allowed.length > 1;
  const isMaster = me?.role === "master";
  // A single-category admin has nothing to aggregate — keep them on their one category.
  useEffect(() => {
    if (!data) return;
    if (!showAllTab && tab === "all") setTab(allowed[0] || "all");
  }, [data, showAllTab]);
  const closedCount = (data?.submissions || []).filter((s) => !s.archived && s.status === "closed").length;
  const archivedCount = (data?.submissions || []).filter((s) => s.archived).length;
  const canArchive = me?.role === "master" || (me?.permissions || []).includes("archive");
  // Archived submissions are hidden by default; the "Show archived" toggle re-includes them
  // (never in the Needs-attention view, which is only for open items).
  const baseList = useMemo(() => {
    const all = data?.submissions || [];
    if (attention) return all.filter((s) => !s.archived && s.status !== "closed");
    return showArchived ? all : all.filter((s) => !s.archived);
  }, [data, attention, showArchived]);
  const filtered = useMemo(() => {
    let list = baseList;
    if (tab !== "all") list = list.filter((s) => s.type === tab);
    if (statusFilter !== "all") list = list.filter((s) => s.status === statusFilter);
    if (!attention && hideClosed) list = list.filter((s) => s.status !== "closed");
    if (q.trim()) list = list.filter((s) => searchMatch(q, submissionSearchText(s)));
    return list;
  }, [baseList, tab, q, hideClosed, statusFilter, attention]);
  const attentionTotal = baseList.length;
  const statusOptions = attention
    ? [{ value: "all", label: `All (${attentionTotal})` }, { value: "new", label: "New" }, { value: "reviewed", label: "Reviewed" }, { value: "actioned", label: "Actioned" }]
    : [{ value: "all", label: "All statuses" }, { value: "new", label: "New" }, { value: "reviewed", label: "Reviewed" }, { value: "actioned", label: "Actioned" }, { value: "closed", label: "Closed" }];
  // Tab counts are computed from the current archived scope so the badge never misleads
  // vs. what's actually shown (archived items are excluded unless "Show archived" is on).
  const counts = useMemo(() => {
    const c = {};
    baseList.forEach((s) => { c[s.type] = (c[s.type] || 0) + 1; });
    return c;
  }, [baseList]);
  const total = baseList.length;
  // Open the AI Respond flow and auto-promote the submission to "reviewed".
  const openRespond = async (s) => {
    setResponding(s);
    if (s.status === "new") { try { await markReviewed(token, s.id); load(); } catch { /* non-blocking */ } }
  };
  const [banner, setBanner] = useState("");
  const doReopen = async (s) => {
    try { await reopenSubmission(token, s.id); load(); }
    catch (e) { setBanner(errMsg(e, "Could not reopen this submission.")); }
  };
  const doUnarchive = async (s) => {
    try { await unarchiveSubmission(token, s.id); load(); }
    catch (e) { setBanner(errMsg(e, "Could not restore this submission.")); }
  };

  const [repOpen, setRepOpen] = useState(false);
  const [repCat, setRepCat] = useState("all");
  const [repRange, setRepRange] = useState("all");
  const [repStart, setRepStart] = useState("");
  const [repEnd, setRepEnd] = useState("");
  const [repBusy, setRepBusy] = useState("");
  const [repAudit, setRepAudit] = useState(false);
  const repRef = useRef(null);
  useEffect(() => {
    const h = (e) => { if (repRef.current && !repRef.current.contains(e.target)) setRepOpen(false); };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);
  const rangeDates = (r) => {
    const now = new Date(); const iso = (d) => d.toISOString().slice(0, 10);
    if (r === "custom") return { start: repStart || undefined, end: repEnd || undefined };
    if (r === "month") return { start: iso(new Date(now.getFullYear(), now.getMonth(), 1)), end: iso(now) };
    if (r === "30") { const s = new Date(now); s.setDate(s.getDate() - 29); return { start: iso(s), end: iso(now) }; }
    if (r === "90") { const s = new Date(now); s.setDate(s.getDate() - 89); return { start: iso(s), end: iso(now) }; }
    if (r === "year") return { start: iso(new Date(now.getFullYear(), 0, 1)), end: iso(now) };
    return {};
  };
  const genReport = async (fmt) => {
    setRepBusy(fmt);
    try {
      const { start, end } = rangeDates(repRange);
      triggerDownload(submissionsExportUrl(token, fmt, repCat, start, end, repAudit));
      setRepOpen(false);
    } catch { /* surfaced by disabled state */ } finally { setRepBusy(""); }
  };
  const repSelectCls = "mt-1 h-10 w-full rounded-lg border border-[var(--a-border)] bg-[var(--a-field)] px-3 text-sm text-[var(--a-text)] shadow-inner transition-colors duration-200 focus:border-[#D9A93E] focus:outline-none focus:ring-2 focus:ring-[#D9A93E]/40";

  const tabBtn = (id, label, count) => (
    <button key={id} onClick={() => setTab(id)} data-testid={`admin-tab-${id}`}
      className={`inline-flex w-auto shrink-0 items-center justify-start gap-2 whitespace-nowrap rounded-lg px-3.5 py-2 text-sm font-medium transition-colors duration-150 ${tab === id ? "bg-[var(--a-card)] text-[var(--a-text)] shadow-sm ring-1 ring-[var(--a-border)]" : "text-[var(--a-text-muted)] hover:text-[var(--a-text2)] hover:bg-[var(--a-hover)]"}`}>
      <span>{label}</span>
      {count != null && <span className={`grid h-5 min-w-[1.375rem] place-items-center rounded-md px-1.5 text-xs font-semibold tabular-nums ${tab === id ? "bg-[#D9A93E] text-[#050B14]" : "bg-[var(--a-chip)] text-[var(--a-text-muted)]"}`}>{count}</span>}
    </button>
  );

  return (
    <div data-testid="admin-submissions">
      <div className="mb-6 flex flex-col gap-4">
        <div className="flex gap-1.5 overflow-x-auto no-scrollbar rounded-xl border border-[var(--a-border)] bg-[var(--a-surface2)] p-1.5 sm:flex-wrap sm:items-center sm:self-start sm:overflow-visible" data-testid="admin-filter-tabs">
          {showAllTab && tabBtn("all", "All", total)}
          {CATEGORIES.filter((c) => allowed.includes(c.id)).map((c) => tabBtn(c.id, c.label, counts[c.id]))}
        </div>
        <div className="flex flex-col gap-3">
          <div className="relative w-full">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-[var(--a-text-dim)]" />
            <Input data-testid="admin-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search submissions…" className={`h-10 w-full rounded-lg border-[var(--a-border)] bg-[var(--a-field)] pl-9 text-sm text-[var(--a-text)] placeholder:text-[var(--a-text-dim)] shadow-inner`} />
          </div>
          <div className="flex flex-col gap-2 sm:flex-row sm:flex-wrap sm:items-center">
          <AdminSelect value={statusFilter} onValueChange={setStatusFilter} testId="status-filter" ariaLabel="Filter by status" triggerClassName="h-10 w-full sm:w-[180px]" options={statusOptions} />
          {!attention && closedCount > 0 && (
            <label className="flex shrink-0 cursor-pointer items-center gap-2 whitespace-nowrap rounded-lg border border-[var(--a-border)] bg-[var(--a-surface2)] px-3 py-2 text-sm text-[var(--a-text2)]" data-testid="hide-closed-toggle">
              <input type="checkbox" checked={hideClosed} onChange={(e) => setHideClosed(e.target.checked)} className="h-4 w-4 rounded border-[var(--a-border)] bg-[var(--a-field)] accent-[#D9A93E]" />
              Hide closed ({closedCount})
            </label>
          )}
          {!attention && archivedCount > 0 && (
            <label className="flex shrink-0 cursor-pointer items-center gap-2 whitespace-nowrap rounded-lg border border-[var(--a-border)] bg-[var(--a-surface2)] px-3 py-2 text-sm text-[var(--a-text2)]" data-testid="show-archived-toggle">
              <input type="checkbox" checked={showArchived} onChange={(e) => setShowArchived(e.target.checked)} className="h-4 w-4 rounded border-[var(--a-border)] bg-[var(--a-field)] accent-[#D9A93E]" />
              Show archived ({archivedCount})
            </label>
          )}
          {canReports && (
          <div className="relative shrink-0" ref={repRef}>
            <button onClick={() => { setRepCat(tab); setRepOpen((o) => !o); }} data-testid="admin-generate-report" className={btnSecondary}>
              <FileText className="h-4 w-4" /> <span className="hidden sm:inline">Generate</span> Report
              <ChevronDown className={`h-4 w-4 transition-transform ${repOpen ? "rotate-180" : ""}`} />
            </button>
            {repOpen && (
              <div className="absolute right-0 z-50 mt-2 w-80 max-w-[calc(100vw-2rem)] rounded-xl border border-[var(--a-border)] bg-[var(--a-surface2)] p-4 shadow-2xl" data-testid="report-panel">
                <label className={labelCls}>What to include</label>
                <AdminSelect value={repCat} onValueChange={setRepCat} testId="report-category-select" triggerClassName="mt-1 h-10 w-full"
                  options={[...(showAllTab ? [{ value: "all", label: "All submissions" }] : []), ...CATEGORIES.filter((c) => allowed.includes(c.id)).map((c) => ({ value: c.id, label: c.label }))]} />
                <label className={`${labelCls} mt-3`}>Time frame</label>
                <AdminSelect value={repRange} onValueChange={setRepRange} testId="report-range-select" triggerClassName="mt-1 h-10 w-full"
                  options={[{ value: "all", label: "All time" }, { value: "month", label: "This month" }, { value: "30", label: "Last 30 days" }, { value: "90", label: "Last 90 days" }, { value: "year", label: "This year" }, { value: "custom", label: "Custom range…" }]} />
                {repRange === "custom" && (
                  <div className="mt-2 grid grid-cols-2 gap-2" data-testid="report-custom-range">
                    <div className="min-w-0">
                      <label className="block text-[10px] font-medium uppercase tracking-wide text-[var(--a-text-dim)]">From</label>
                      <input type="date" value={repStart} max={repEnd || undefined} onChange={(e) => setRepStart(e.target.value)} data-testid="report-start-date" className={`${repSelectCls} min-w-0`} />
                    </div>
                    <div className="min-w-0">
                      <label className="block text-[10px] font-medium uppercase tracking-wide text-[var(--a-text-dim)]">To</label>
                      <input type="date" value={repEnd} min={repStart || undefined} onChange={(e) => setRepEnd(e.target.value)} data-testid="report-end-date" className={`${repSelectCls} min-w-0`} />
                    </div>
                  </div>
                )}
                <label className="mt-3 flex cursor-pointer items-start gap-2 rounded-lg border border-[var(--a-border)] bg-[var(--a-surface2)] px-3 py-2.5" data-testid="report-audit-toggle">
                  <input type="checkbox" checked={repAudit} onChange={(e) => setRepAudit(e.target.checked)} className="mt-0.5 h-4 w-4 rounded border-[var(--a-border)] bg-[var(--a-field)] accent-[#D9A93E]" />
                  <span className="text-xs text-[var(--a-text2)]">Include audit trail<span className="block text-[10px] text-[var(--a-text-dim)]">Adds a separate column with each record&apos;s full status history</span></span>
                </label>
                <label className={`${labelCls} mt-3`}>Format</label>
                <div className="mt-1 flex gap-2">
                  <button onClick={() => genReport("pdf")} disabled={!!repBusy} data-testid="report-pdf-btn" className={`${btnGold} min-w-0 flex-1 whitespace-nowrap px-2`}>{repBusy === "pdf" ? <Loader2 className="h-4 w-4 animate-spin" /> : <><FileText className="h-4 w-4 shrink-0" /> PDF</>}</button>
                  <button onClick={() => genReport("csv")} disabled={!!repBusy} data-testid="report-csv-btn" className={`${btnSecondary} min-w-0 flex-1 whitespace-nowrap px-2`}>{repBusy === "csv" ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Download className="h-4 w-4 shrink-0" /> CSV</>}</button>
                  <button onClick={() => genReport("excel")} disabled={!!repBusy} data-testid="report-excel-btn" className={`${btnSecondary} min-w-0 flex-1 whitespace-nowrap px-2`}>{repBusy === "excel" ? <Loader2 className="h-4 w-4 animate-spin" /> : <><FileSpreadsheet className="h-4 w-4 shrink-0" /> Excel</>}</button>
                </div>
              </div>
            )}
          </div>
          )}
          </div>
        </div>
      </div>

      {banner && (
        <div className="mb-4 flex items-center justify-between gap-3 rounded-lg border border-rose-400/30 bg-rose-400/10 px-4 py-2.5 text-sm text-rose-300" data-testid="submissions-banner">
          <span className="flex items-center gap-2"><AlertTriangle className="h-4 w-4" /> {banner}</span>
          <button onClick={() => setBanner("")} className="text-rose-300/70 hover:text-rose-300"><X className="h-4 w-4" /></button>
        </div>
      )}

      {loading ? (
        <div className="grid place-items-center py-24 text-[var(--a-text-dim)]"><Loader2 className="h-8 w-8 animate-spin text-[var(--a-gold-ink)]" /></div>
      ) : filtered.length === 0 ? (
        <div className={`${card} grid place-items-center gap-2 py-24 text-[var(--a-text-dim)]`} data-testid="admin-empty">
          <Inbox className="h-8 w-8 text-[var(--a-text-dim)]" strokeWidth={1.5} />
          <p className="text-sm">No submissions found.</p>
        </div>
      ) : (
        <div className="grid min-w-0 gap-4 md:grid-cols-2 xl:grid-cols-3" data-testid="admin-list">
          {filtered.map((s) => (
            <article key={s.id} className={`${card} group flex min-w-0 flex-col overflow-hidden p-5 transition-[transform,border-color] duration-300 hover:-translate-y-0.5 hover:border-[var(--a-border2)]`} data-testid="admin-card">
              <div className="flex flex-wrap items-start justify-between gap-2">
                <span className={`rounded-md px-2 py-0.5 text-[0.65rem] font-semibold uppercase tracking-wide ring-1 ${TYPE_COLOR[s.type] || "bg-[var(--a-hover)] text-[var(--a-text2)] ring-[var(--a-border)]"}`}>{TYPE_LABEL[s.type] || s.type}</span>
                <StatusPill status={s.status} onClick={() => setHistoryFor(s)} testId={`status-pill-${s.id}`} />
              </div>
              <h3 className="mt-3 font-heading text-base font-semibold text-[var(--a-text)]">{displayName(s)}</h3>
              <time className="mt-0.5 block text-xs text-[var(--a-text-dim)]">{fmtDate(s.created_at)}</time>
              {s.follow_up && s.status !== "closed" && (() => {
                const today = new Date().toLocaleDateString("en-CA");
                const due = s.follow_up <= today;
                return (
                  <span data-testid={`followup-chip-${s.id}`} className={`mt-2 inline-flex w-fit items-center gap-1.5 rounded-md px-2 py-0.5 text-[0.68rem] font-semibold ring-1 ${due ? "bg-rose-400/15 text-rose-300 ring-rose-400/30" : "bg-[#D9A93E]/12 text-[var(--a-gold-ink)] ring-[#D9A93E]/25"}`}>
                    <Clock className="h-3 w-3" /> {due ? "Follow-up due" : "Follow-up"} · {fmtDateOnly(s.follow_up)}
                  </span>
                );
              })()}
              <CardFields s={s} />
              {s.responses?.filter((r) => !r.archived).length > 0 && (
                <div className="mt-4 border-t border-[var(--a-border)] pt-3" data-testid={`followups-${s.id}`}>
                  <button type="button" onClick={() => setFollowupsForId(s.id)} data-testid={`followups-toggle-${s.id}`}
                    className="group/fu flex w-full items-center gap-2 text-sm font-bold text-emerald-600 transition-colors hover:text-emerald-500">
                    <Send className="h-4 w-4 shrink-0" strokeWidth={2} />
                    <span>Follow-up history</span>
                    <span className="grid h-5 min-w-[1.25rem] place-items-center rounded-full bg-emerald-600/15 px-1.5 text-xs font-semibold tabular-nums text-emerald-600">{s.responses.filter((r) => !r.archived).length}</span>
                    <ArrowUpRight className="ml-auto h-4 w-4 text-[var(--a-text-dim)] transition-transform group-hover/fu:-translate-y-0.5 group-hover/fu:translate-x-0.5 group-hover/fu:text-emerald-500" />
                  </button>
                </div>
              )}
              {s.archived ? (
                <div className="mt-5 flex flex-wrap items-center justify-between gap-2 rounded-lg border border-[var(--a-border)] bg-[var(--a-surface2)] px-3 py-2.5" data-testid={`archived-footer-${s.id}`}>
                  <span className="flex min-w-0 items-center gap-1.5 text-xs font-medium text-[var(--a-text2)]"><Archive className="h-3.5 w-3.5 shrink-0" /> <span className="truncate">Archived{s.archive_reason ? ` — ${s.archive_reason}` : ""}</span></span>
                  {canArchive && (
                    <button onClick={() => doUnarchive(s)} data-testid={`unarchive-${s.id}`} className="inline-flex shrink-0 items-center gap-1.5 rounded-md border border-[var(--a-border)] px-2.5 py-1 text-xs font-semibold text-[var(--a-gold-ink)] transition-colors hover:border-[#D9A93E]/50"><ArchiveRestore className="h-3.5 w-3.5" /> Restore</button>
                  )}
                </div>
              ) : s.status === "closed" ? (
                <div className="mt-5 flex flex-wrap items-center justify-between gap-2 rounded-lg border border-[var(--a-border)] bg-[var(--a-surface2)] px-3 py-2.5">
                  <span className="flex items-center gap-1.5 text-xs font-medium text-[var(--a-text2)]"><Lock className="h-3.5 w-3.5" /> Closed — record is locked</span>
                  <div className="flex items-center gap-2">
                    {isMaster && (
                      <button onClick={() => doReopen(s)} data-testid={`reopen-${s.id}`} className="inline-flex items-center gap-1.5 rounded-md border border-[var(--a-border)] px-2.5 py-1 text-xs font-semibold text-[var(--a-gold-ink)] transition-colors hover:border-[#D9A93E]/50"><Undo2 className="h-3.5 w-3.5" /> Reopen</button>
                    )}
                    {canArchive && (
                      <button onClick={() => setArchiveFor(s)} data-testid={`archive-${s.id}`} title="Move to archive" className="inline-flex items-center gap-1.5 rounded-md border border-[var(--a-border)] px-2.5 py-1 text-xs font-semibold text-[var(--a-text2)] transition-colors hover:border-[#D9A93E]/50 hover:text-[var(--a-gold-ink)]"><Archive className="h-3.5 w-3.5" /> Archive</button>
                    )}
                  </div>
                </div>
              ) : (
                <div className="mt-5 flex flex-col gap-2">
                  <button onClick={() => openRespond(s)} data-testid={`respond-${s.id}`} className={`${btnGold} w-full`}>
                    <Sparkles className="h-4 w-4" /> Respond
                  </button>
                  <div className="flex gap-2">
                    <button onClick={() => setLogFor(s)} data-testid={`log-action-${s.id}`} className={`${btnSecondary} flex-1`}>
                      <ListPlus className="h-4 w-4" /> {s.status === "actioned" ? "Add action" : "Log action"}
                    </button>
                    <button onClick={() => setCloseFor(s)} data-testid={`close-sub-${s.id}`} className={`${btnSecondary} flex-1`}>
                      <CheckCircle2 className="h-4 w-4" /> Close
                    </button>
                    {canArchive && (
                      <button onClick={() => setArchiveFor(s)} data-testid={`archive-${s.id}`} title="Move to archive" className={`${btnSecondary} px-2.5`}>
                        <Archive className="h-4 w-4" />
                      </button>
                    )}
                  </div>
                </div>
              )}
            </article>
          ))}
        </div>
      )}
      {responding && <RespondModal token={token} submission={responding} onClose={() => setResponding(null)} onSent={(sid, info) => { setResponding(null); setSentInfo(info); load(); }} />}
      {historyFor && <HistoryModal submission={historyFor} onClose={() => setHistoryFor(null)} />}
      {followupsForId && (() => {
        const sub = (data?.submissions || []).find((x) => x.id === followupsForId);
        return sub ? <FollowupHistoryModal token={token} submission={sub} onClose={() => setFollowupsForId(null)} onChange={load} /> : null;
      })()}
      {logFor && <LogActionModal token={token} submission={logFor} onClose={() => setLogFor(null)} onDone={() => { setLogFor(null); load(); }} />}
      {closeFor && <CloseModal token={token} submission={closeFor} onClose={() => setCloseFor(null)} onDone={() => { setCloseFor(null); load(); }} />}
      {archiveFor && <ArchiveSubmissionModal token={token} submission={archiveFor} onClose={() => setArchiveFor(null)} onDone={() => { setArchiveFor(null); load(); }} />}
      <SentPopup info={sentInfo} onClose={() => setSentInfo(null)} />
    </div>
  );
}

/* ============================ Admins (master) ============================ */
const AUDIT_SUB_OPTIONS = [
  { id: "audit_activity", label: "Activity feed + 'Activity log' report section" },
  { id: "audit_auth", label: "Sign-in & security events" },
  { id: "audit_admins", label: "Admin accounts & access" },
  { id: "audit_submissions", label: "Submissions handling" },
  { id: "audit_processing", label: "Submissions processing performance" },
  { id: "audit_donations", label: "Donations & goals" },
  { id: "audit_summaries", label: "Summary emails sent" },
  { id: "audit_reports", label: "Reports generated" },
];
const ARCHIVE_SUB_OPTIONS = [
  { id: "archive_submissions", label: "Archived submissions" },
  { id: "archive_reports", label: "Archived reports" },
  { id: "archive_summaries", label: "Archived summary emails" },
  { id: "archive_audit", label: "Archived audit reports" },
];
const PARENT_META = {
  archive: { label: "Archive access", subs: ARCHIVE_SUB_OPTIONS, legacy: "archive", icon: Archive },
  audit: { label: "Activity log & audit", subs: AUDIT_SUB_OPTIONS, legacy: "audit", icon: ShieldCheck },
};

function SubPermModal({ parent, selected, setSelected, idPrefix, onClose }) {
  const { label, subs, legacy, icon } = PARENT_META[parent];
  const legacyFull = selected.includes(legacy);
  const isOn = (id) => legacyFull || selected.includes(id);
  const normalize = () => legacyFull ? selected.filter((x) => x !== legacy).concat(subs.map((s) => s.id)) : [...selected];
  const toggleSub = (id) => {
    const base = normalize();
    setSelected(base.includes(id) ? base.filter((x) => x !== id) : [...base, id]);
  };
  const allOn = subs.every((s) => isOn(s.id));
  const toggleAll = () => {
    const cleared = selected.filter((x) => x !== legacy && !subs.some((s) => s.id === x));
    setSelected(allOn ? cleared : [...cleared, ...subs.map((s) => s.id)]);
  };
  return (
    <Overlay onClose={onClose} testId={`${idPrefix}-subperm-modal`} maxW="modal-w-md">
      <ModalHeader icon={icon} title={label} subtitle="Tick exactly what this admin can access — only the areas you choose will show for them." onClose={onClose} testId={`${idPrefix}-subperm-close`} />
      <div className="flex-1 overflow-y-auto px-5 py-5">
        <div className="mb-3 flex items-center justify-between">
          <p className="text-xs text-[var(--a-text-muted)]">{subs.filter((s) => isOn(s.id)).length} of {subs.length} selected</p>
          <button type="button" onClick={toggleAll} data-testid={`${idPrefix}-subperm-toggle-all`} className="text-xs font-semibold text-[var(--a-gold-ink)] hover:underline">{allOn ? "Clear all" : "Select all"}</button>
        </div>
        <div className="space-y-1.5">
          {subs.map((s) => {
            const on = isOn(s.id);
            return (
              <button type="button" key={s.id} onClick={() => toggleSub(s.id)} data-testid={`${idPrefix}-subperm-${s.id}`}
                className={`flex w-full items-center gap-2.5 rounded-lg border px-3 py-2.5 text-left text-sm transition-colors ${on ? "border-[#D9A93E] bg-[#D9A93E]/10 text-[var(--a-text)]" : "border-[var(--a-border)] bg-[var(--a-surface2)] text-[var(--a-text2)] hover:border-[var(--a-border2)]"}`}>
                <span className={`grid h-4 w-4 shrink-0 place-items-center rounded border ${on ? "border-[#D9A93E] bg-[#D9A93E] text-[#0F1F3D]" : "border-[var(--a-border2)]"}`}>{on && <Check className="h-3 w-3" strokeWidth={3} />}</span>
                {s.label}
              </button>
            );
          })}
        </div>
      </div>
      <div className="flex shrink-0 justify-end border-t border-[var(--a-border)] px-5 py-4"><button type="button" onClick={onClose} className={btnGold}>Done</button></div>
    </Overlay>
  );
}

function PermChips({ selected, setSelected, idPrefix }) {
  const [config, setConfig] = useState(null);
  const toggle = (id) => setSelected(selected.includes(id) ? selected.filter((x) => x !== id) : [...selected, id]);
  const chipCls = (on) => `inline-flex w-full items-center gap-1.5 rounded-lg px-3 py-2 text-xs font-medium ring-1 transition-colors ${on ? "bg-[#D9A93E]/15 text-[var(--a-gold-ink)] ring-[#D9A93E]/40" : "bg-[var(--a-surface2)] text-[var(--a-text-muted)] ring-[var(--a-border)] hover:text-[var(--a-text2)]"}`;
  const subCount = (parent) => { const { subs, legacy } = PARENT_META[parent]; return selected.includes(legacy) ? subs.length : subs.filter((s) => selected.includes(s.id)).length; };
  return (
    <div className="space-y-4">
      {["Submissions", "Donations & Reports"].map((g) => (
        <div key={g}>
          <p className="mb-2 text-[0.62rem] font-semibold uppercase tracking-wide text-[var(--a-text-dim)]">{g}</p>
          <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
            {PERMISSIONS.filter((p) => p.group === g).map((c) => (
              <button type="button" key={c.id} onClick={() => toggle(c.id)} data-testid={`${idPrefix}-perm-${c.id}`} className={`${chipCls(selected.includes(c.id))} justify-start`}>
                {selected.includes(c.id) && <Check className="h-3 w-3 shrink-0" />}<span className="truncate">{c.label}</span>
              </button>
            ))}
          </div>
        </div>
      ))}
      <div>
        <p className="mb-2 text-[0.62rem] font-semibold uppercase tracking-wide text-[var(--a-text-dim)]">Other · click to choose areas</p>
        <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
          {["archive", "audit"].map((parent) => {
            const { label, subs } = PARENT_META[parent];
            const n = subCount(parent);
            return (
              <button type="button" key={parent} onClick={() => setConfig(parent)} data-testid={`${idPrefix}-perm-${parent}`} className={`${chipCls(n > 0)} justify-start`}>
                {n > 0 && <Check className="h-3 w-3 shrink-0" />}<span className="truncate">{label}</span>
                <span className="ml-auto flex shrink-0 items-center gap-1">
                  {n > 0 && <span className="rounded bg-[#D9A93E]/20 px-1.5 py-0.5 text-[0.6rem] font-bold text-[var(--a-gold-ink)]">{n}/{subs.length}</span>}
                  <Settings className="h-3.5 w-3.5 opacity-60" />
                </span>
              </button>
            );
          })}
        </div>
      </div>
      {config && <SubPermModal parent={config} selected={selected} setSelected={setSelected} idPrefix={idPrefix} onClose={() => setConfig(null)} />}
    </div>
  );
}

function AdminsView({ token }) {
  const [admins, setAdmins] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [form, setForm] = useState({ email: "", password: "", permissions: [] });
  const [busy, setBusy] = useState(false);
  const reload = useCallback(() => { getAdmins(token).then((d) => setAdmins(d.admins)).catch(() => {}).finally(() => setLoading(false)); }, [token]);
  useEffect(() => { reload(); }, [reload]);

  const add = async (e) => {
    e.preventDefault(); setError(""); setBusy(true);
    try { await createAdmin(token, form); setForm({ email: "", password: "", permissions: [] }); reload(); }
    catch (err) { setError(errMsg(err, "Could not add admin.")); }
    finally { setBusy(false); }
  };
  const savePerms = async (a, perms) => { await updateAdmin(token, a.id, perms); reload(); };
  const remove = async (a) => { if (!window.confirm(`Remove ${a.email}?`)) return; await deleteAdmin(token, a.id); reload(); };

  return (
    <div className="max-w-5xl" data-testid="admin-admins">
      <form onSubmit={add} className={`${card} mb-8 p-6`} data-testid="admin-add-form">
        <h3 className="flex items-center gap-2 font-heading text-lg font-semibold tracking-tight"><UserPlus className="h-5 w-5 text-[var(--a-gold-ink)]" strokeWidth={1.75} /> Add an admin</h3>
        <p className="mt-1 text-sm text-[var(--a-text-muted)]">Grant access to specific submission categories, donations, and reports. They&apos;ll set their own password on first sign-in.</p>
        <div className="mt-5 grid gap-4 sm:grid-cols-2">
          <div><label className={labelCls}>Email</label><Input data-testid="new-admin-email" type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="new.admin@thefrent.com" className={`mt-1.5 ${fieldCls}`} /></div>
          <div><label className={labelCls}>Temporary password</label><PasswordInput testId="new-admin-password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} placeholder="Temporary password" className={`mt-1.5 ${fieldCls}`} /></div>
        </div>
        <div className="mt-4"><p className={`${labelCls} mb-2`}>Permissions</p><PermChips selected={form.permissions} setSelected={(ids) => setForm((f) => ({ ...f, permissions: ids }))} idPrefix="new-admin" /></div>
        {error && <p className="mt-4 rounded-lg border border-rose-500/20 bg-rose-500/10 px-3 py-2 text-sm text-rose-300" data-testid="admin-add-error">{error}</p>}
        <button type="submit" disabled={busy} data-testid="admin-add-submit" className={`mt-5 ${btnGold}`}>{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Add Admin"}</button>
      </form>

      {loading ? <div className="grid place-items-center py-16 text-[var(--a-text-dim)]"><Loader2 className="h-7 w-7 animate-spin text-[var(--a-gold-ink)]" /></div> : (
        <div className="grid gap-4" data-testid="admin-admins-list">
          {admins.map((a) => (
            <div key={a.id} className={`${card} p-5`} data-testid="admin-row">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div>
                  <div className="flex items-center gap-2">
                    <h4 className="font-heading text-base font-semibold text-[var(--a-text)]">{a.email}</h4>
                    <span className={`rounded-md px-2 py-0.5 text-[0.62rem] font-bold uppercase tracking-wide ${a.role === "master" ? "bg-[#D9A93E] text-[#050B14]" : "bg-white/8 text-[var(--a-text2)]"}`}>{a.role}</span>
                  </div>
                  {a.created_by && <p className="mt-0.5 text-xs text-[var(--a-text-dim)]">Added by {a.created_by}</p>}
                </div>
                {a.role !== "master" && (
                  <button onClick={() => remove(a)} data-testid={`admin-remove-${a.email}`} className="inline-flex items-center gap-1.5 rounded-lg border border-rose-500/25 px-3 py-1.5 text-xs font-semibold text-rose-300 transition-colors hover:bg-rose-500/10">
                    <Trash2 className="h-3.5 w-3.5" /> Remove
                  </button>
                )}
              </div>
              <div className="mt-4">
                <p className={`${labelCls} mb-2`}>Access</p>
                {a.role === "master" ? (
                  <p className="text-sm text-[var(--a-text-muted)]">Full access to everything.</p>
                ) : (
                  <EditablePerms admin={a} onSave={savePerms} />
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function EditablePerms({ admin, onSave }) {
  const [perms, setPerms] = useState(admin.permissions || []);
  const [saving, setSaving] = useState(false);
  const dirty = JSON.stringify([...perms].sort()) !== JSON.stringify([...(admin.permissions || [])].sort());
  const save = async () => { setSaving(true); try { await onSave(admin, perms); } finally { setSaving(false); } };
  return (
    <div className="flex flex-wrap items-center gap-3">
      <PermChips selected={perms} setSelected={setPerms} idPrefix={`edit-${admin.email}`} />
      {dirty && <button onClick={save} disabled={saving} data-testid={`admin-save-${admin.email}`} className="inline-flex items-center gap-1.5 rounded-lg bg-[#D9A93E] px-3.5 py-1.5 text-xs font-semibold text-[#050B14] transition-colors hover:bg-[#E0B25A]">{saving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : "Save"}</button>}
    </div>
  );
}

/* ============================ Settings ============================ */
/* ============================ Donations (master) ============================ */
function AccessDenied({ onBack }) {
  return (
    <div className="fixed inset-0 z-[70] grid place-items-center bg-black/70 px-4 backdrop-blur-sm" data-testid="access-denied-modal">
      <div className="w-full modal-w-md rounded-2xl border border-[var(--a-border)] bg-[var(--a-surface2)] p-6 text-center shadow-2xl sm:p-8">
        <span className="mx-auto grid h-14 w-14 place-items-center rounded-full bg-rose-500/12 text-rose-300 ring-1 ring-rose-500/25"><Lock className="h-6 w-6" strokeWidth={1.75} /></span>
        <h2 className="mt-4 font-heading text-xl font-semibold text-[var(--a-text)]">Access restricted</h2>
        <p className="mt-2 text-sm leading-relaxed text-[var(--a-text-muted)]">You don&apos;t have permission to view this section. If you believe this is a mistake, please contact your administrator.</p>
        <button onClick={onBack} data-testid="access-denied-back" className={`${btnGold} mx-auto mt-6`}>Back to Submissions</button>
      </div>
    </div>
  );
}

function Collapsible({ title, icon: Icon, count, defaultOpen = true, testId, children }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className={`${card} overflow-hidden`} data-testid={testId}>
      <button type="button" onClick={() => setOpen((o) => !o)} data-testid={testId ? `${testId}-toggle` : undefined}
        className={`group flex w-full items-center justify-between gap-3 p-6 text-left transition-colors duration-200 hover:bg-[var(--a-hover-soft)] ${open ? "border-b border-[var(--a-border)]" : ""}`}>
        <h3 className="flex items-center gap-3 font-heading text-lg font-semibold tracking-tight text-[var(--a-text)]">
          {Icon && <span className="grid h-9 w-9 shrink-0 place-items-center rounded-lg bg-[#D9A93E]/12 text-[var(--a-gold-ink)] ring-1 ring-[#D9A93E]/20 transition-colors group-hover:bg-[#D9A93E]/20"><Icon className="h-5 w-5" strokeWidth={1.75} /></span>}
          {title}{typeof count === "number" && <span className="text-sm font-normal text-[var(--a-text-dim)]">({count})</span>}
        </h3>
        <ChevronDown className={`h-5 w-5 shrink-0 text-[var(--a-text-muted)] transition-transform duration-300 group-hover:text-[var(--a-gold-ink)] ${open ? "rotate-180" : ""}`} />
      </button>
      {open && <div className="p-6">{children}</div>}
    </div>
  );
}

function ArchiveReportModal({ report, onCancel, onConfirm, busy }) {
  const [remarks, setRemarks] = useState("");
  return (
    <div className="fixed inset-0 z-[70] grid place-items-center bg-black/70 px-4 backdrop-blur-sm" data-testid="archive-modal" onClick={onCancel}>
      <div className="w-full modal-w-md rounded-2xl border border-[var(--a-border)] bg-[var(--a-surface2)] p-6 shadow-2xl" onClick={(e) => e.stopPropagation()}>
        <h3 className="font-heading text-lg font-semibold text-[var(--a-text)]">Archive report</h3>
        <p className="mt-1 text-sm text-[var(--a-text-muted)] [overflow-wrap:anywhere]">{report.filename}</p>
        <label className="mt-4 block text-xs font-semibold uppercase tracking-wide text-[var(--a-text-dim)]">Remarks</label>
        <textarea value={remarks} onChange={(e) => setRemarks(e.target.value)} rows={3} data-testid="archive-remarks" placeholder="Add a note about why this report is being archived…" className="mt-1 w-full rounded-lg border border-[var(--a-border)] bg-[var(--a-bg)] px-3 py-2 text-sm text-[var(--a-text)] focus:border-[#D9A93E] focus:outline-none focus:ring-2 focus:ring-[#D9A93E]/40" />
        <div className="mt-5 flex justify-end gap-2">
          <button onClick={onCancel} className={btnSecondary}>Cancel</button>
          <button onClick={() => onConfirm(remarks)} disabled={busy} data-testid="archive-confirm" className={btnGold}>{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Archive className="h-4 w-4" /> Archive</>}</button>
        </div>
      </div>
    </div>
  );
}

function ReportRow({ token, report, onArchive }) {
  const [open, setOpen] = useState(false);
  const [st, setSt] = useState({ loading: false, url: null, text: null, error: null });
  const Icon = report.format === "pdf" ? FileText : report.format === "excel" ? FileSpreadsheet : Download;
  const toggle = async () => {
    if (open) { if (st.url) URL.revokeObjectURL(st.url); setSt({ loading: false, url: null, text: null, error: null }); setOpen(false); return; }
    setOpen(true); setSt({ loading: true, url: null, text: null, error: null });
    try {
      const blob = await getReportBlob(token, report.id);
      const url = URL.createObjectURL(blob);
      const text = report.format === "csv" ? await blob.text() : null;
      setSt({ loading: false, url, text, error: null });
    } catch { setSt({ loading: false, url: null, text: null, error: "Could not load this report." }); }
  };
  const download = () => triggerDownload(reportContentUrl(token, report.id, true));
  return (
    <div className={`rounded-xl border ${report.archived ? "border-[var(--a-border)] opacity-70" : "border-[var(--a-border)]"} bg-[var(--a-surface2)]`} data-testid={`report-${report.id}`}>
      <div className="flex flex-wrap items-center gap-3 p-4">
        <span className="grid h-9 w-9 shrink-0 place-items-center rounded-lg bg-[var(--a-hover)] text-[var(--a-gold-ink)]"><Icon className="h-4 w-4" /></span>
        <button onClick={toggle} data-testid={`report-toggle-${report.id}`} className="flex min-w-0 flex-1 items-center justify-between gap-3 text-left">
          <span className="min-w-0">
            <span className="block truncate text-sm font-medium text-[var(--a-text)]">{report.scope_label} <span className="uppercase text-[var(--a-gold-ink)]">· {report.format}</span></span>
            <span className="block text-xs text-[var(--a-text-dim)] [overflow-wrap:anywhere]">{fmtDate(report.generated_at)} · {report.generated_by}{report.archived ? " · Archived" : ""}</span>
          </span>
          <ChevronDown className={`h-4 w-4 shrink-0 text-[var(--a-text-muted)] transition-transform ${open ? "rotate-180" : ""}`} />
        </button>
        {report.archived
          ? <button onClick={() => onArchive(report, false)} data-testid={`unarchive-${report.id}`} title="Unarchive" className="grid h-8 w-8 shrink-0 place-items-center rounded-lg text-[var(--a-text-muted)] transition-colors hover:bg-[var(--a-hover)] hover:text-emerald-300"><ArchiveRestore className="h-4 w-4" /></button>
          : <button onClick={() => onArchive(report, true)} data-testid={`archive-${report.id}`} title="Archive" className="grid h-8 w-8 shrink-0 place-items-center rounded-lg text-[var(--a-text-muted)] transition-colors hover:bg-[var(--a-hover)] hover:text-amber-300"><Archive className="h-4 w-4" /></button>}
      </div>
      {report.archived && report.remarks && <p className="border-t border-[var(--a-border)] px-4 py-2 text-xs text-[var(--a-text-muted)]"><span className="font-semibold text-[var(--a-text2)]">Remarks:</span> {report.remarks}</p>}
      {open && (
        <div className="border-t border-[var(--a-border)] p-4">
          {st.loading ? <p className="text-sm text-[var(--a-text-dim)]">Loading…</p>
            : st.error ? <p className="text-sm text-rose-300">{st.error}</p>
            : (
              <div>
                <div className="mb-3 flex justify-end"><button onClick={download} data-testid={`report-download-${report.id}`} className={btnSecondary}><Download className="h-4 w-4" /> Download</button></div>
                {report.format === "pdf" && st.url && <iframe title={report.filename} src={st.url} className="h-[60vh] w-full rounded-lg border border-[var(--a-border)] bg-white" data-testid={`report-preview-${report.id}`} />}
                {report.format === "csv" && <pre className="max-h-[50vh] overflow-auto rounded-lg border border-[var(--a-border)] bg-[var(--a-bg)] p-3 text-xs leading-relaxed text-[var(--a-text2)]" data-testid={`report-preview-${report.id}`}>{st.text}</pre>}
                {report.format === "excel" && <p className="text-sm text-[var(--a-text-muted)]">Excel files can&apos;t preview in the browser — use Download to open it.</p>}
              </div>
            )}
        </div>
      )}
    </div>
  );
}

// Equal-width stacking for filter pills/bars: when peer controls sit one above another
// (any viewport), every row shares the SAME width as the widest one — the narrower rows
// stretch to match. NOTE: do NOT wrap a search bar here (a search bar is meant to be the
// widest on its own); use this only for peer filter controls (segmented pills, dropdowns).
function EqualStack({ children, className = "", testId }) {
  return (
    <div data-testid={testId} className={`flex w-fit max-w-full flex-col items-stretch gap-3 ${className}`}>
      {children}
    </div>
  );
}


function ReportsView({ token }) {
  const [reports, setReports] = useState(null);
  const [archiving, setArchiving] = useState(null);
  const [busy, setBusy] = useState(false);
  const [fmtFilter, setFmtFilter] = useState("all");
  const [dateFilter, setDateFilter] = useState("all");
  const [showArchived, setShowArchived] = useState(false);
  const load = useCallback(() => getReports(token).then((d) => setReports(d.reports)).catch(() => setReports([])), [token]);
  useEffect(() => { load(); }, [load]);
  const onArchive = (report, archived) => {
    if (archived) { setArchiving(report); return; }
    archiveReport(token, report.id, false, "").then(load);
  };
  const confirmArchive = async (remarks) => {
    setBusy(true);
    try { await archiveReport(token, archiving.id, true, remarks); setArchiving(null); await load(); } finally { setBusy(false); }
  };
  const groups = [
    { id: "submissions", label: "Submission reports", icon: Inbox },
    { id: "donations", label: "Donation reports", icon: HeartHandshake },
  ];
  const cutoffFor = (val) => {
    if (val === "all") return null;
    const d = new Date();
    if (val === "month") { d.setDate(1); d.setHours(0, 0, 0, 0); return d; }
    if (val === "30") { d.setDate(d.getDate() - 30); return d; }
    if (val === "90") { d.setDate(d.getDate() - 90); return d; }
    return null;
  };
  const cutoff = cutoffFor(dateFilter);
  const matches = (r) => {
    if (fmtFilter !== "all" && r.format !== fmtFilter) return false;
    if (!showArchived && r.archived) return false;
    if (cutoff && r.generated_at && new Date(r.generated_at) < cutoff) return false;
    return true;
  };
  const archivedCount = (reports || []).filter((r) => r.archived).length;
  return (
    <div className="space-y-6" data-testid="admin-reports">
      {/* Filters */}
      <div className={`${card} p-5`} data-testid="reports-filters">
        <div className="flex flex-wrap items-end gap-4">
          <div className="flex flex-col gap-1">
            <label className={labelCls}>Format</label>
            <AdminSelect value={fmtFilter} onValueChange={setFmtFilter} testId="report-format-filter" triggerClassName="h-10 w-[168px]"
              options={[{ value: "all", label: "All formats" }, { value: "pdf", label: "PDF" }, { value: "csv", label: "CSV" }, { value: "excel", label: "Excel" }]} />
          </div>
          <div className="flex flex-col gap-1">
            <label className={labelCls}>Generated</label>
            <AdminSelect value={dateFilter} onValueChange={setDateFilter} testId="report-date-filter" triggerClassName="h-10 w-[168px]"
              options={[{ value: "all", label: "All time" }, { value: "month", label: "This month" }, { value: "30", label: "Last 30 days" }, { value: "90", label: "Last 90 days" }]} />
          </div>
          <label className="flex cursor-pointer items-center gap-2 pb-2 text-sm text-[var(--a-text2)]" data-testid="report-show-archived">
            <input type="checkbox" checked={showArchived} onChange={(e) => setShowArchived(e.target.checked)} className="h-4 w-4 rounded border-[var(--a-border)] bg-[var(--a-bg)] text-[var(--a-gold-ink)] accent-[#D9A93E]" />
            Show archived{archivedCount ? ` (${archivedCount})` : ""}
          </label>
        </div>
      </div>
      {reports === null ? <p className="text-sm text-[var(--a-text-dim)]">Loading…</p> : groups.map((g) => {
        const items = reports.filter((r) => r.category === g.id && matches(r));
        const Icon = g.icon;
        return (
          <Collapsible key={g.id} title={g.label} icon={Icon} count={items.length} testId={`reports-group-${g.id}`}>
            {items.length === 0
              ? <p className="text-sm text-[var(--a-text-dim)]">No reports match these filters. Generate one from the {g.id === "donations" ? "Donations" : "Submissions"} tab.</p>
              : <div className="space-y-3">{items.map((r) => <ReportRow key={r.id} token={token} report={r} onArchive={onArchive} />)}</div>}
          </Collapsible>
        );
      })}
      {archiving && <ArchiveReportModal report={archiving} busy={busy} onCancel={() => setArchiving(null)} onConfirm={confirmArchive} />}
    </div>
  );
}

function CommunicationModal({ comm, donor, onClose }) {
  return (
    <div className="fixed inset-0 z-[70] grid place-items-center bg-black/70 px-4 py-8 backdrop-blur-sm" data-testid="communication-modal" onClick={onClose}>
      <div className="flex max-h-[85vh] w-full modal-w-2xl flex-col overflow-hidden rounded-2xl border border-[var(--a-border)] bg-[var(--a-surface2)] shadow-2xl" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between gap-3 border-b border-[var(--a-border)] px-5 py-4">
          <div className="min-w-0">
            <p className="font-heading text-base font-semibold text-[var(--a-text)] [overflow-wrap:anywhere]">{comm.subject}</p>
            <p className="mt-0.5 text-xs text-[var(--a-text-muted)] [overflow-wrap:anywhere]">To {donor || "donor"} · Sent {fmtDate(comm.sent_at)}</p>
          </div>
          <button onClick={onClose} data-testid="communication-close" className="grid h-8 w-8 shrink-0 place-items-center rounded-lg text-[var(--a-text-muted)] transition-colors hover:bg-[var(--a-hover)]"><X className="h-4 w-4" /></button>
        </div>
        <div className="min-h-0 flex-1 overflow-hidden bg-white">
          <iframe title="Sent confirmation email" srcDoc={comm.html} sandbox="" className="h-[60vh] w-full border-0" data-testid="communication-iframe" />
        </div>
      </div>
    </div>
  );
}

function DonationsList({ token }) {
  const [rows, setRows] = useState(null);
  const [open, setOpen] = useState({});
  const [modal, setModal] = useState(null);
  const [q, setQ] = useState("");
  const [dateMode, setDateMode] = useState("all");
  const [specificDate, setSpecificDate] = useState("");
  const [rangeStart, setRangeStart] = useState("");
  const [rangeEnd, setRangeEnd] = useState("");
  useEffect(() => { getDonationsList(token).then((d) => setRows(d.donations)).catch(() => setRows([])); }, [token]);
  const money = (n) => `$${Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  const dayET = (iso) => { try { return new Date(iso).toLocaleDateString("en-CA", { timeZone: "America/New_York" }); } catch { return ""; } };
  const cutoffMs = () => {
    const now = Date.now();
    if (dateMode === "24h") return now - 24 * 60 * 60 * 1000;
    if (dateMode === "2w") return now - 14 * 24 * 60 * 60 * 1000;
    if (dateMode === "1m") return now - 30 * 24 * 60 * 60 * 1000;
    return null;
  };
  const cutoff = cutoffMs();
  const matchDate = (d) => {
    if (!d.date) return dateMode === "all";
    if (cutoff != null) { const t = new Date(d.date).getTime(); return !isNaN(t) && t >= cutoff; }
    if (dateMode === "specific") { if (!specificDate) return true; return dayET(d.date) === specificDate; }
    if (dateMode === "range") { const day = dayET(d.date); if (rangeStart && day < rangeStart) return false; if (rangeEnd && day > rangeEnd) return false; return true; }
    return true;
  };
  const matchSearch = (d) => searchMatch(q, d.donor_name, d.donor_email);
  const filtered = (rows || []).filter((d) => matchSearch(d) && matchDate(d));
  const searchCls = "w-full rounded-lg border border-[var(--a-border)] bg-[var(--a-field)] py-2 pl-9 pr-3 text-sm text-[var(--a-text)] placeholder:text-[var(--a-text-dim)] shadow-inner transition-colors duration-200 focus:border-[#D9A93E] focus:outline-none focus:ring-2 focus:ring-[#D9A93E]/40";
  const dateInputCls = "h-10 rounded-lg border border-[var(--a-border)] bg-[var(--a-field)] px-3 text-sm text-[var(--a-text)] shadow-inner transition-colors duration-200 focus:border-[#D9A93E] focus:outline-none focus:ring-2 focus:ring-[#D9A93E]/40";
  const activeDateFilter = dateMode !== "all" && !(dateMode === "specific" && !specificDate) && !(dateMode === "range" && !rangeStart && !rangeEnd);
  return (
    <Collapsible title="All donations" icon={HeartHandshake} count={rows ? rows.length : undefined} testId="donations-list-card">
      <p className="text-sm text-[var(--a-text-muted)]">Every completed gift with donor details and the confirmation we emailed them.</p>
      <div className="relative mt-3">
        <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-[var(--a-text-dim)]" />
        <input value={q} onChange={(e) => setQ(e.target.value)} data-testid="donation-search" placeholder="Search donations by donor name or email…" className={searchCls} />
      </div>
      {/* Date filter */}
      <div className="mt-3 flex flex-wrap items-center gap-2" data-testid="donation-date-filter">
        <AdminSelect value={dateMode} onValueChange={setDateMode} testId="donation-date-mode" ariaLabel="Filter donations by date" triggerClassName="h-10 w-[190px]"
          options={[{ value: "all", label: "All time" }, { value: "24h", label: "A day ago" }, { value: "2w", label: "Last two weeks" }, { value: "1m", label: "A month ago" }, { value: "specific", label: "A specific date…" }, { value: "range", label: "A date range…" }]} />
        {dateMode === "specific" && (
          <input type="date" value={specificDate} onChange={(e) => setSpecificDate(e.target.value)} data-testid="donation-date-specific" className={dateInputCls} aria-label="Specific date" />
        )}
        {dateMode === "range" && (
          <div className="flex flex-wrap items-center gap-2">
            <input type="date" value={rangeStart} max={rangeEnd || undefined} onChange={(e) => setRangeStart(e.target.value)} data-testid="donation-date-start" className={dateInputCls} aria-label="From date" />
            <span className="text-xs text-[var(--a-text-dim)]">to</span>
            <input type="date" value={rangeEnd} min={rangeStart || undefined} onChange={(e) => setRangeEnd(e.target.value)} data-testid="donation-date-end" className={dateInputCls} aria-label="To date" />
          </div>
        )}
        {activeDateFilter && (
          <button type="button" onClick={() => { setDateMode("all"); setSpecificDate(""); setRangeStart(""); setRangeEnd(""); }} data-testid="donation-date-clear" className="inline-flex items-center gap-1 rounded-lg border border-[var(--a-border)] bg-[var(--a-field)] px-2.5 py-1.5 text-xs font-medium text-[var(--a-text2)] transition-colors hover:border-[#D9A93E]/50 hover:text-[var(--a-gold-ink)]">
            <X className="h-3.5 w-3.5" /> Clear
          </button>
        )}
      </div>
      {rows === null ? <p className="mt-4 text-sm text-[var(--a-text-dim)]">Loading…</p>
        : rows.length === 0 ? <p className="mt-4 text-sm text-[var(--a-text-dim)]">No donations yet.</p>
        : filtered.length === 0 ? <p className="mt-4 text-sm text-[var(--a-text-dim)]" data-testid="donation-search-empty">No donations match your filters.</p>
        : (
          <div className="mt-4 space-y-3">
            <p className="text-xs text-[var(--a-text-dim)]" data-testid="donation-filter-count">Showing {filtered.length} of {rows.length} gift{rows.length === 1 ? "" : "s"}.</p>
            {filtered.map((d) => (
              <div key={d.id} className="min-w-0 rounded-xl border border-[var(--a-border)] bg-[var(--a-surface2)] p-4 transition-colors duration-200 hover:border-[var(--a-border2)]" data-testid={`donation-${d.id}`}>
                <div className="flex flex-wrap items-baseline justify-between gap-x-3 gap-y-1">
                  <p className="font-medium text-[var(--a-text)]">{d.donor_name || "Anonymous donor"} <span className="ml-1 text-[var(--a-gold-ink)]">{money(d.amount)}</span></p>
                  <p className="text-xs text-[var(--a-text-muted)]">{fmtDate(d.date)}{d.frequency === "monthly" ? " · Monthly" : ""}</p>
                </div>
                <p className="mt-0.5 text-xs text-[var(--a-text-dim)] [overflow-wrap:anywhere]">{d.donor_email || "No email on file"}</p>
                <div className="mt-3 border-t border-[var(--a-border)] pt-3">
                  <button onClick={() => setOpen((o) => ({ ...o, [d.id]: !o[d.id] }))} data-testid={`comm-toggle-${d.id}`} className="flex w-full items-center justify-between text-left text-[0.7rem] font-semibold uppercase tracking-wide text-[var(--a-text-muted)] transition-colors hover:text-[var(--a-text2)]">
                    <span className="flex items-center gap-1.5"><Mail className="h-3.5 w-3.5" /> Communications from organization</span>
                    <ChevronDown className={`h-4 w-4 transition-transform ${open[d.id] ? "rotate-180" : ""}`} />
                  </button>
                  {open[d.id] && (
                    <div className="mt-2">
                      {d.communication ? (
                        <button onClick={() => setModal({ comm: d.communication, donor: d.donor_email })} data-testid={`comm-view-${d.id}`} className="flex w-full items-center justify-between gap-2 rounded-lg border border-[var(--a-border)] bg-[var(--a-bg)] px-3 py-2 text-left text-sm text-[var(--a-text2)] transition-colors hover:border-[#D9A93E]/40">
                          <span className="min-w-0"><span className="block truncate font-medium">{d.communication.subject}</span><span className="text-xs text-[var(--a-text-dim)]">Sent {fmtDate(d.communication.sent_at)}</span></span>
                          <Eye className="h-4 w-4 shrink-0 text-[var(--a-gold-ink)]" />
                        </button>
                      ) : <p className="text-xs text-[var(--a-text-dim)]">No confirmation email was sent for this gift.</p>}
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      {modal && <CommunicationModal comm={modal.comm} donor={modal.donor} onClose={() => setModal(null)} />}
    </Collapsible>
  );
}

function SummaryRow({ token, r, onArchive, onRestore, selectable, selected, onToggleSelect }) {
  const [open, setOpen] = useState(false);
  const manual = (r.trigger || "system") === "manual";
  const archived = !!r.archived;
  return (
    <div className={`min-w-0 rounded-xl border bg-[var(--a-surface2)] p-4 transition-colors duration-200 hover:border-[var(--a-border2)] ${selected ? "border-[#D9A93E] ring-1 ring-[#D9A93E]/40" : "border-[var(--a-border)]"} ${archived ? "opacity-80" : ""}`} data-testid={`summary-${r.id}`}>
      <div className="flex flex-wrap items-baseline justify-between gap-x-3 gap-y-1">
        <p className="flex items-center gap-2 font-medium text-[var(--a-text)]">
          {selectable && (
            <button type="button" onClick={() => onToggleSelect(r.id)} data-testid={`summary-select-${r.id}`} aria-label="Select summary"
              className={`grid h-4 w-4 shrink-0 place-items-center self-center rounded border transition-colors ${selected ? "border-[#D9A93E] bg-[#D9A93E] text-[#0F1F3D]" : "border-[var(--a-border2)] hover:border-[#D9A93E]"}`}>
              {selected && <Check className="h-3 w-3" strokeWidth={3} />}
            </button>
          )}
          {r.label} summary
          <span data-testid={`summary-pill-${r.id}`} className={`rounded-md px-2 py-0.5 text-[0.62rem] font-bold uppercase tracking-wide ring-1 ${manual ? "bg-[#D9A93E]/15 text-[var(--a-gold-ink)] ring-[#D9A93E]/30" : "bg-sky-500/15 text-sky-600 ring-sky-500/30"}`}>
            {manual ? "Manual" : "System"}
          </span>
        </p>
        <p className="text-xs text-[var(--a-text-muted)]">Sent {fmtDate(r.sent_at)}</p>
      </div>
      <p className="mt-0.5 text-xs text-[var(--a-text-dim)] [overflow-wrap:anywhere]">{r.subject}</p>
      <p className="mt-0.5 text-xs text-[var(--a-text-dim)]">By {r.sent_by || "System"} · to {(r.recipients || []).length} recipient{(r.recipients || []).length === 1 ? "" : "s"}</p>
      {archived && <p className="mt-0.5 text-[0.7rem] text-[var(--a-text-dim)]">Archived by {r.archived_by || "—"}{r.archived_at ? ` · ${fmtDate(r.archived_at)}` : ""}</p>}
      {(r.attachments || []).length > 0 && (
        <div className="mt-3 border-t border-[var(--a-border)] pt-3" data-testid={`summary-reports-${r.id}`}>
          <p className="mb-2 flex items-center gap-1.5 text-[0.7rem] font-semibold uppercase tracking-wide text-[var(--a-text-muted)]"><FileText className="h-3.5 w-3.5" /> Summary reports attached</p>
          <div className="grid gap-2 sm:grid-cols-2">
            {r.attachments.map((a, i) => (
              <div key={i} className="flex min-w-0 items-center gap-3 rounded-lg border border-[var(--a-border)] bg-[var(--a-bg)] px-3 py-2.5" data-testid={`summary-att-${r.id}-${i}`}>
                <span className="grid h-9 w-9 shrink-0 place-items-center rounded-lg bg-[#D9A93E]/12 text-[var(--a-gold-ink)] ring-1 ring-[#D9A93E]/20"><FileText className="h-4 w-4" strokeWidth={1.75} /></span>
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-medium text-[var(--a-text2)]">{a.label || a.filename}</p>
                  <p className="truncate text-[0.7rem] text-[var(--a-text-dim)]">PDF · {a.filename}</p>
                </div>
                <div className="flex shrink-0 items-center gap-1">
                  <button onClick={() => triggerDownload(summaryAttachmentUrl(token, r.id, i, false))} data-testid={`summary-att-view-${r.id}-${i}`} title="View report" className="grid h-8 w-8 place-items-center rounded-md text-[var(--a-text-muted)] transition-colors hover:bg-[var(--a-hover)] hover:text-[var(--a-gold-ink)]"><Eye className="h-4 w-4" /></button>
                  <button onClick={() => triggerDownload(summaryAttachmentUrl(token, r.id, i, true))} data-testid={`summary-att-download-${r.id}-${i}`} title="Download report" className="grid h-8 w-8 place-items-center rounded-md text-[var(--a-text-muted)] transition-colors hover:bg-[var(--a-hover)] hover:text-[var(--a-gold-ink)]"><Download className="h-4 w-4" /></button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
      <div className="mt-3 border-t border-[var(--a-border)] pt-3">
        <button onClick={() => setOpen((o) => !o)} data-testid={`summary-toggle-${r.id}`} className="flex w-full items-center justify-between text-left text-[0.7rem] font-semibold uppercase tracking-wide text-[var(--a-text-muted)] transition-colors hover:text-[var(--a-text2)]">
          <span className="flex items-center gap-1.5"><Mail className="h-3.5 w-3.5" /> {open ? "Hide email" : "View email"}</span>
          <ChevronDown className={`h-4 w-4 transition-transform ${open ? "rotate-180" : ""}`} />
        </button>
        {open && (
          <div className="mt-2 overflow-hidden rounded-lg border border-[var(--a-border)] bg-white" data-testid={`summary-email-${r.id}`}>
            <iframe title={`${r.label} summary email`} srcDoc={r.html} sandbox="" className="h-[440px] w-full border-0" />
          </div>
        )}
      </div>
      {archived ? (
        <div className="mt-3 flex flex-wrap items-center justify-between gap-2 rounded-lg border border-[var(--a-border)] bg-[var(--a-bg)] px-3 py-2.5" data-testid={`summary-archived-footer-${r.id}`}>
          <span className="flex min-w-0 items-center gap-1.5 text-xs font-medium text-[var(--a-text2)]"><Archive className="h-3.5 w-3.5 shrink-0" /> <span className="[overflow-wrap:anywhere]">Archived{r.archive_reason ? ` — ${r.archive_reason}` : ""}</span></span>
          {onRestore && <button onClick={() => onRestore(r)} data-testid={`summary-unarchive-${r.id}`} className="inline-flex shrink-0 items-center gap-1.5 rounded-md border border-[var(--a-border)] px-2.5 py-1 text-xs font-semibold text-[var(--a-gold-ink)] transition-colors hover:border-[#D9A93E]/50"><ArchiveRestore className="h-3.5 w-3.5" /> Restore</button>}
        </div>
      ) : (onArchive && (
        <div className="mt-3 flex justify-end">
          <button onClick={() => onArchive(r)} data-testid={`summary-archive-${r.id}`} className="inline-flex items-center gap-1.5 rounded-lg border border-[var(--a-border)] bg-[var(--a-bg)] px-3 py-1.5 text-xs font-semibold text-[var(--a-text2)] transition-colors hover:border-[#D9A93E]/50 hover:text-[var(--a-gold-ink)]"><Archive className="h-3.5 w-3.5" /> Archive</button>
        </div>
      ))}
    </div>
  );
}

const SUMMARY_ARCHIVE_REASONS = ["No longer needed", "Duplicate summary", "Sent in error", "Superseded by a corrected summary", "Routine cleanup", "Other"];

function ArchiveSummaryModal({ title = "Archive summary email", subtitle, onConfirm, onClose }) {
  const [reason, setReason] = useState("");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const submit = async () => {
    if (!reason) { setError("Please choose a reason for archiving."); return; }
    if (reason === "Other" && !note.trim()) { setError("Please describe the reason in the note."); return; }
    setBusy(true); setError("");
    try {
      const finalReason = reason === "Other" ? note.trim() : (note.trim() ? `${reason} — ${note.trim()}` : reason);
      await onConfirm(finalReason);
    } catch (e) { setError(errMsg(e, "Could not archive.")); setBusy(false); }
  };
  return (
    <Overlay onClose={onClose} testId="archive-summary-modal" maxW="modal-w-md">
      <ModalHeader icon={Archive} title={title} subtitle={subtitle} onClose={onClose} testId="archive-summary-close" />
      <div className="flex-1 overflow-y-auto px-5 py-5">
        <label className={labelCls}>Reason for archiving</label>
        <div className="mt-2 grid gap-1.5 sm:grid-cols-2" data-testid="summary-archive-reason-options">
          {SUMMARY_ARCHIVE_REASONS.map((rr) => (
            <button type="button" key={rr} onClick={() => setReason(rr)} data-testid={`summary-archive-reason-${rr.toLowerCase().replace(/[^a-z]+/g, "-").replace(/^-|-$/g, "")}`}
              className={`flex items-center gap-2 rounded-lg border px-3 py-2.5 text-left text-sm transition-colors ${reason === rr ? "border-[#D9A93E] bg-[#D9A93E]/10 text-[var(--a-text)]" : "border-[var(--a-border)] bg-[var(--a-surface2)] text-[var(--a-text2)] hover:border-[var(--a-border2)]"}`}>
              <span className={`grid h-4 w-4 shrink-0 place-items-center rounded-full border ${reason === rr ? "border-[#D9A93E]" : "border-[var(--a-border2)]"}`}>{reason === rr && <span className="h-2 w-2 rounded-full bg-[#D9A93E]" />}</span>
              {rr}
            </button>
          ))}
        </div>
        <label className={`${labelCls} mt-4`}>{reason === "Other" ? "Explain (required)" : "Note (optional)"}</label>
        <textarea value={note} onChange={(e) => setNote(e.target.value)} rows={3} data-testid="summary-archive-note"
          placeholder={reason === "Other" ? "Describe why this is being archived…" : "Add any extra context for the record…"}
          className="mt-2 w-full rounded-lg border border-[var(--a-border)] bg-[var(--a-field)] px-3 py-2 text-sm text-[var(--a-text)] shadow-inner focus:border-[#D9A93E] focus:outline-none focus:ring-2 focus:ring-[#D9A93E]/40" />
        {error && <p className="mt-3 rounded-lg border border-rose-400/30 bg-rose-400/10 px-3 py-2 text-sm text-rose-300" data-testid="summary-archive-error">{error}</p>}
      </div>
      <div className="flex shrink-0 justify-end gap-2 border-t border-[var(--a-border)] px-5 py-4">
        <button onClick={onClose} className={btnSecondary}>Cancel</button>
        <button onClick={submit} disabled={busy} data-testid="summary-archive-submit" className={btnGold}>{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Archive className="h-4 w-4" /> Archive</>}</button>
      </div>
    </Overlay>
  );
}

function SummaryHistory({ token, refreshKey }) {
  const [rows, setRows] = useState(null);
  const [typeFilter, setTypeFilter] = useState("all");
  const [dateMode, setDateMode] = useState("all");
  const [specificDate, setSpecificDate] = useState("");
  const [rangeStart, setRangeStart] = useState("");
  const [rangeEnd, setRangeEnd] = useState("");
  const [bump, setBump] = useState(0);
  const [q, setQ] = useState("");
  const [archiveTarget, setArchiveTarget] = useState(null);
  const [selected, setSelected] = useState(new Set());
  const [bulkOpen, setBulkOpen] = useState(false);
  useEffect(() => { getSummaries(token).then((d) => setRows(d.summaries || [])).catch(() => setRows([])); }, [token, refreshKey, bump]);
  const toggleSelect = (id) => setSelected((prev) => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const reload = () => { setSelected(new Set()); setBump((n) => n + 1); };

  const dayET = (iso) => { try { return new Date(iso).toLocaleDateString("en-CA", { timeZone: "America/New_York" }); } catch { return ""; } };
  const cutoffMs = () => {
    const now = Date.now();
    if (dateMode === "24h") return now - 24 * 60 * 60 * 1000;
    if (dateMode === "2w") return now - 14 * 24 * 60 * 60 * 1000;
    if (dateMode === "1m") return now - 30 * 24 * 60 * 60 * 1000;
    return null;
  };
  const cutoff = cutoffMs();
  const matchDate = (r) => {
    const iso = r.sent_at;
    if (!iso) return dateMode === "all";
    if (cutoff != null) { const t = new Date(iso).getTime(); return !isNaN(t) && t >= cutoff; }
    if (dateMode === "specific") { if (!specificDate) return true; return dayET(iso) === specificDate; }
    if (dateMode === "range") { const day = dayET(iso); if (rangeStart && day < rangeStart) return false; if (rangeEnd && day > rangeEnd) return false; return true; }
    return true;
  };
  const matchType = (r) => typeFilter === "all" || (r.trigger || "system") === typeFilter;
  const matchSearch = (r) => searchMatch(q, r.label, r.sent_by, r.subject);
  // Archived summaries live in the Archive tab — keep the history tidy by hiding them here.
  const active = (rows || []).filter((r) => !r.archived);
  const filtered = active.filter((r) => matchType(r) && matchDate(r) && matchSearch(r));
  const activeDateFilter = dateMode !== "all" && !(dateMode === "specific" && !specificDate) && !(dateMode === "range" && !rangeStart && !rangeEnd);
  const dateInputCls = "h-10 rounded-lg border border-[var(--a-border)] bg-[var(--a-field)] px-3 text-sm text-[var(--a-text)] shadow-inner transition-colors duration-200 focus:border-[#D9A93E] focus:outline-none focus:ring-2 focus:ring-[#D9A93E]/40";
  const TYPE_TABS = [{ v: "all", l: "All" }, { v: "system", l: "System-generated" }, { v: "manual", l: "Manual" }];

  return (
    <Collapsible title="Summary email history" icon={History} count={rows ? active.length : undefined} testId="summary-history-card">
      <p className="text-sm text-[var(--a-text-muted)]">Every monthly summary email that was sent — automatically on the 1st (System) or manually by an admin. Archive old ones to keep this list tidy; they move to the Archive tab.</p>

      {/* Search (naturally widest — not equalized with the filters below it) */}
      <div className="relative mt-4">
        <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-[var(--a-text-dim)]" />
        <input value={q} onChange={(e) => setQ(e.target.value)} data-testid="summary-search" placeholder="Search summaries by month or who sent them…"
          className="w-full rounded-lg border border-[var(--a-border)] bg-[var(--a-field)] py-2 pl-9 pr-3 text-sm text-[var(--a-text)] placeholder:text-[var(--a-text-dim)] shadow-inner transition-colors duration-200 focus:border-[#D9A93E] focus:outline-none focus:ring-2 focus:ring-[#D9A93E]/40" />
      </div>

      {/* Peer filters — equalized to the widest via EqualStack */}
      <EqualStack className="mt-3">
        <div className="inline-flex flex-wrap gap-1 rounded-xl border border-[var(--a-border)] bg-[var(--a-surface2)] p-1" data-testid="summary-type-filter">
          {TYPE_TABS.map((t) => (
            <button key={t.v} type="button" onClick={() => setTypeFilter(t.v)} data-testid={`summary-type-${t.v}`}
              className={`flex-1 whitespace-nowrap rounded-lg px-3 py-1.5 text-center text-xs font-semibold transition-colors ${typeFilter === t.v ? "bg-[#D9A93E] text-[#0F1F3D]" : "text-[var(--a-text-muted)] hover:text-[var(--a-text2)]"}`}>
              {t.l}
            </button>
          ))}
        </div>
        <div className="flex flex-wrap items-center gap-2" data-testid="summary-date-filter">
          <AdminSelect value={dateMode} onValueChange={setDateMode} testId="summary-date-mode" ariaLabel="Filter summaries by date" triggerClassName="h-10 w-full"
            options={[{ value: "all", label: "All time" }, { value: "24h", label: "A day ago" }, { value: "2w", label: "Last two weeks" }, { value: "1m", label: "A month ago" }, { value: "specific", label: "A specific date…" }, { value: "range", label: "A date range…" }]} />
          {dateMode === "specific" && (
            <input type="date" value={specificDate} onChange={(e) => setSpecificDate(e.target.value)} data-testid="summary-date-specific" className={dateInputCls} aria-label="Specific date" />
          )}
          {dateMode === "range" && (
            <div className="flex flex-wrap items-center gap-2">
              <input type="date" value={rangeStart} max={rangeEnd || undefined} onChange={(e) => setRangeStart(e.target.value)} data-testid="summary-date-start" className={dateInputCls} aria-label="From date" />
              <span className="text-xs text-[var(--a-text-dim)]">to</span>
              <input type="date" value={rangeEnd} min={rangeStart || undefined} onChange={(e) => setRangeEnd(e.target.value)} data-testid="summary-date-end" className={dateInputCls} aria-label="To date" />
            </div>
          )}
          {activeDateFilter && (
            <button type="button" onClick={() => { setDateMode("all"); setSpecificDate(""); setRangeStart(""); setRangeEnd(""); }} data-testid="summary-date-clear" className="inline-flex items-center gap-1 rounded-lg border border-[var(--a-border)] bg-[var(--a-field)] px-2.5 py-1.5 text-xs font-medium text-[var(--a-text2)] transition-colors hover:border-[#D9A93E]/50 hover:text-[var(--a-gold-ink)]">
              <X className="h-3.5 w-3.5" /> Clear
            </button>
          )}
        </div>
      </EqualStack>

      {rows === null ? <p className="mt-4 text-sm text-[var(--a-text-dim)]">Loading…</p>
        : active.length === 0 ? <p className="mt-4 text-sm text-[var(--a-text-dim)]" data-testid="summary-empty">No summary emails to show.</p>
        : filtered.length === 0 ? <p className="mt-4 text-sm text-[var(--a-text-dim)]" data-testid="summary-filter-empty">No summaries match your filters.</p>
        : (
          <div className="mt-4 space-y-3">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <p className="text-xs text-[var(--a-text-dim)]" data-testid="summary-filter-count">Showing {filtered.length} of {active.length} summar{active.length === 1 ? "y" : "ies"}.</p>
              {selected.size > 0 && (
                <div className="flex items-center gap-2" data-testid="summary-bulk-bar">
                  <span className="text-xs font-semibold text-[var(--a-text2)]" data-testid="summary-bulk-count">{selected.size} selected</span>
                  <button type="button" onClick={() => setBulkOpen(true)} data-testid="summary-bulk-archive-btn" className="inline-flex items-center gap-1.5 rounded-lg bg-[#D9A93E] px-3 py-1.5 text-xs font-semibold text-[#0F1F3D] transition-opacity hover:opacity-90"><Archive className="h-3.5 w-3.5" /> Archive selected</button>
                  <button type="button" onClick={() => setSelected(new Set())} data-testid="summary-bulk-clear" className="inline-flex items-center gap-1 rounded-lg border border-[var(--a-border)] px-2.5 py-1.5 text-xs font-medium text-[var(--a-text2)] transition-colors hover:border-[#D9A93E]/50"><X className="h-3.5 w-3.5" /> Clear</button>
                </div>
              )}
            </div>
            {filtered.map((r) => <SummaryRow key={r.id} token={token} r={r} onArchive={setArchiveTarget} selectable selected={selected.has(r.id)} onToggleSelect={toggleSelect} />)}
          </div>
        )}
      {archiveTarget && <ArchiveSummaryModal subtitle={`${archiveTarget.label} summary — give a reason so the archive stays auditable. You can restore it anytime.`}
        onConfirm={async (reason) => { await archiveSummary(token, archiveTarget.id, true, reason); setArchiveTarget(null); reload(); }} onClose={() => setArchiveTarget(null)} />}
      {bulkOpen && <ArchiveSummaryModal subtitle={`Archiving ${selected.size} summar${selected.size === 1 ? "y" : "ies"} — the same reason applies to all. You can restore any of them later.`}
        onConfirm={async (reason) => { for (const id of selected) { await archiveSummary(token, id, true, reason); } setBulkOpen(false); reload(); }} onClose={() => setBulkOpen(false)} />}
    </Collapsible>
  );
}

const AUDIT_SECTIONS = [
  { id: "activity", label: "Activity log", desc: "Every recorded admin action" },
  { id: "auth", label: "Sign-in & security events", desc: "Sign-ins, failed attempts, password changes" },
  { id: "admins", label: "Admin accounts & access", desc: "Who has access and what they can do" },
  { id: "submissions", label: "Submissions handling", desc: "Counts by category and status" },
  { id: "processing", label: "Submissions processing performance", desc: "How fast submissions move review → action → close" },
  { id: "donations", label: "Donations & goals", desc: "Monthly raised vs goal" },
  { id: "summaries", label: "Summary emails sent", desc: "Monthly summaries and who sent them" },
  { id: "reports", label: "Reports generated", desc: "Exported reports and who made them" },
];
const AUDIT_CAT_PILL = {
  auth: "bg-sky-500/15 text-sky-600 ring-sky-500/30",
  admins: "bg-violet-500/15 text-violet-600 ring-violet-500/30",
  submissions: "bg-emerald-500/15 text-emerald-600 ring-emerald-500/30",
  communications: "bg-teal-500/15 text-teal-600 ring-teal-500/30",
  donations: "bg-[#D9A93E]/15 text-[var(--a-gold-ink)] ring-[#D9A93E]/30",
  reports: "bg-indigo-500/15 text-indigo-600 ring-indigo-500/30",
  summaries: "bg-rose-500/15 text-rose-600 ring-rose-500/30",
  audit: "bg-slate-500/15 text-slate-600 ring-slate-500/30",
  general: "bg-slate-500/15 text-slate-600 ring-slate-500/30",
};

function AuditReportRow({ token, r, onArchive, onRestore }) {
  const archived = !!r.archived;
  return (
    <div className="rounded-xl border border-[var(--a-border)] bg-[var(--a-surface2)] p-4 transition-colors hover:border-[var(--a-border2)]" data-testid={`audit-report-${r.id}`}>
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div className="min-w-0">
          <p className="flex items-center gap-2 font-medium text-[var(--a-text)]"><ShieldCheck className="h-4 w-4 shrink-0 text-[var(--a-gold-ink)]" /> Audit report · {r.scope}</p>
          <p className="mt-0.5 text-xs text-[var(--a-text-dim)]">Generated {fmtDate(r.generated_at)} by {r.generated_by} · {Math.max(1, Math.round((r.size || 0) / 1024))} KB</p>
        </div>
        <div className="flex shrink-0 items-center gap-1">
          <button onClick={() => triggerDownload(auditReportContentUrl(token, r.id, false))} data-testid={`audit-report-view-${r.id}`} title="View report" className="grid h-8 w-8 place-items-center rounded-md text-[var(--a-text-muted)] transition-colors hover:bg-[var(--a-hover)] hover:text-[var(--a-gold-ink)]"><Eye className="h-4 w-4" /></button>
          <button onClick={() => triggerDownload(auditReportContentUrl(token, r.id, true))} data-testid={`audit-report-download-${r.id}`} title="Download report" className="grid h-8 w-8 place-items-center rounded-md text-[var(--a-text-muted)] transition-colors hover:bg-[var(--a-hover)] hover:text-[var(--a-gold-ink)]"><Download className="h-4 w-4" /></button>
        </div>
      </div>
      <div className="mt-2 flex flex-wrap gap-1.5">
        {(r.section_labels || []).map((s, i) => <span key={i} className="rounded-md bg-[var(--a-bg)] px-2 py-0.5 text-[0.65rem] text-[var(--a-text-muted)] ring-1 ring-[var(--a-border)]">{s}</span>)}
      </div>
      {archived ? (
        <div className="mt-3 flex flex-wrap items-center justify-between gap-2 rounded-lg border border-[var(--a-border)] bg-[var(--a-bg)] px-3 py-2.5">
          <span className="flex min-w-0 items-center gap-1.5 text-xs text-[var(--a-text2)]"><Archive className="h-3.5 w-3.5 shrink-0" /> <span className="[overflow-wrap:anywhere]">Archived{r.archive_reason ? ` — ${r.archive_reason}` : ""}</span></span>
          {onRestore && <button onClick={() => onRestore(r)} data-testid={`audit-report-unarchive-${r.id}`} className="inline-flex shrink-0 items-center gap-1.5 rounded-md border border-[var(--a-border)] px-2.5 py-1 text-xs font-semibold text-[var(--a-gold-ink)] transition-colors hover:border-[#D9A93E]/50"><ArchiveRestore className="h-3.5 w-3.5" /> Restore</button>}
        </div>
      ) : (onArchive && <div className="mt-3 flex justify-end"><button onClick={() => onArchive(r)} data-testid={`audit-report-archive-${r.id}`} className="inline-flex items-center gap-1.5 rounded-lg border border-[var(--a-border)] bg-[var(--a-bg)] px-3 py-1.5 text-xs font-semibold text-[var(--a-text2)] transition-colors hover:border-[#D9A93E]/50 hover:text-[var(--a-gold-ink)]"><Archive className="h-3.5 w-3.5" /> Archive</button></div>)}
    </div>
  );
}

// A single audit-log row. Collapsed by default to a compact one-liner (category pill + summary)
// so a 500-entry feed stays scannable on mobile; the chevron reveals the exact timestamp and, when
// it isn't already in the summary, who performed the action. Keeps the timestamp off the tight top
// row so the pill and time can never collide on narrow screens.
function ActivityEntry({ e, catLabel }) {
  const [open, setOpen] = useState(false);
  const showBy = e.actor_email && !(e.summary || "").includes(e.actor_email);
  return (
    <li className="overflow-hidden rounded-lg border border-[var(--a-border)] bg-[var(--a-surface2)]" data-testid={`activity-entry-${e.id}`}>
      <button type="button" onClick={() => setOpen((o) => !o)} aria-expanded={open} data-testid={`activity-toggle-${e.id}`}
        className="flex w-full items-center gap-2.5 px-3.5 py-2.5 text-left transition-colors hover:bg-[var(--a-hover)]">
        <span className={`shrink-0 rounded-md px-2 py-0.5 text-[0.6rem] font-bold uppercase tracking-wide ring-1 ${AUDIT_CAT_PILL[e.category] || AUDIT_CAT_PILL.general}`}>{catLabel(e.category)}</span>
        <span className={`min-w-0 flex-1 text-sm text-[var(--a-text2)] ${open ? "break-words" : "truncate"}`}>{e.summary}</span>
        <ChevronDown className={`h-4 w-4 shrink-0 text-[var(--a-text-dim)] transition-transform duration-300 ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div className="border-t border-[var(--a-border)] px-3.5 py-2.5 text-xs text-[var(--a-text-dim)]" data-testid={`activity-detail-${e.id}`}>
          <p className="tabular-nums">{fmtDate(e.at)}</p>
          {showBy && <p className="mt-1 break-words">by {e.actor_email}</p>}
        </div>
      )}
    </li>
  );
}


function ActivityLogView({ token, me }) {
  const master = me?.role === "master";
  const mp = me?.permissions || [];
  const fullAudit = master || mp.includes("audit");
  const allowedSecIds = fullAudit ? AUDIT_SECTIONS.map((s) => s.id) : AUDIT_SECTIONS.map((s) => s.id).filter((id) => mp.includes(`audit_${id}`));
  const availableSections = AUDIT_SECTIONS.filter((s) => allowedSecIds.includes(s.id));
  const canFeed = fullAudit || mp.includes("audit_activity");
  const [data, setData] = useState(null);
  const [category, setCategory] = useState("all");
  const [actor, setActor] = useState("all");
  const [q, setQ] = useState("");
  const [start, setStart] = useState("");
  const [end, setEnd] = useState("");
  const [genSections, setGenSections] = useState(() => {
    const d = ["activity", "auth", "admins"].filter((id) => allowedSecIds.includes(id));
    return d.length ? d : allowedSecIds.slice(0, 1);
  });
  const [reports, setReports] = useState(null);
  const [repBump, setRepBump] = useState(0);
  const [repArchive, setRepArchive] = useState(null);
  const [repQ, setRepQ] = useState("");
  const [repCat, setRepCat] = useState("all");
  const [repDateMode, setRepDateMode] = useState("all");
  useEffect(() => {
    if (!canFeed) { setData({ entries: [], actors: [], categories: [] }); return; }
    const params = {};
    if (category !== "all") params.category = category;
    if (actor !== "all") params.actor = actor;
    if (start) params.start = start;
    if (end) params.end = end;
    if (q.trim()) params.q = q.trim();
    getAudit(token, params).then(setData).catch(() => setData({ entries: [], actors: [], categories: [] }));
  }, [token, category, actor, start, end, q, canFeed]);
  useEffect(() => { getAuditReports(token).then((d) => setReports(d.reports || [])).catch(() => setReports([])); }, [token, repBump]);
  const generate = () => { triggerDownload(auditReportUrl(token, genSections, start, end)); setTimeout(() => setRepBump((n) => n + 1), 1600); };
  const activeReports = (reports || []).filter((r) => !r.archived);
  const repCutoff = repDateMode === "24h" ? Date.now() - 864e5 : repDateMode === "2w" ? Date.now() - 14 * 864e5 : repDateMode === "1m" ? Date.now() - 30 * 864e5 : null;
  const filteredReports = activeReports.filter((r) =>
    (searchMatch(repQ, r.scope, r.generated_by, (r.section_labels || []).join(" ")))
    && (repCat === "all" || (r.sections || []).includes(repCat))
    && (repCutoff == null || (new Date(r.generated_at).getTime() >= repCutoff)));
  const entries = data?.entries || [];
  const catLabel = (c) => (data?.categories || []).find((x) => x.value === c)?.label || c;
  const toggleSection = (id) => setGenSections((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));
  const dateCls = "h-10 w-full rounded-lg border border-[var(--a-border)] bg-[var(--a-field)] px-3 text-sm text-[var(--a-text)] shadow-inner focus:border-[#D9A93E] focus:outline-none focus:ring-2 focus:ring-[#D9A93E]/40";
  const hasFilter = category !== "all" || actor !== "all" || start || end || q.trim();

  return (
    <div className="space-y-6">
      {/* Audit report builder */}
      <Collapsible title="Generate audit report" icon={ShieldCheck} testId="audit-report-card">
        <p className="text-sm text-[var(--a-text-muted)]">Pick exactly what to include and an optional date range, then download a polished PDF for your records and oversight.</p>
        <div className="mt-4 grid gap-2 sm:grid-cols-2" data-testid="audit-section-options">
          {availableSections.map((s) => {
            const on = genSections.includes(s.id);
            return (
              <button key={s.id} type="button" onClick={() => toggleSection(s.id)} data-testid={`audit-section-${s.id}`}
                className={`flex items-start gap-2.5 rounded-lg border px-3 py-2.5 text-left transition-colors ${on ? "border-[#D9A93E] bg-[#D9A93E]/10" : "border-[var(--a-border)] bg-[var(--a-surface2)] hover:border-[var(--a-border2)]"}`}>
                <span className={`mt-0.5 grid h-4 w-4 shrink-0 place-items-center rounded border ${on ? "border-[#D9A93E] bg-[#D9A93E] text-[#0F1F3D]" : "border-[var(--a-border2)]"}`}>{on && <Check className="h-3 w-3" strokeWidth={3} />}</span>
                <span className="min-w-0"><span className="block text-sm font-medium text-[var(--a-text)]">{s.label}</span><span className="block text-xs text-[var(--a-text-dim)]">{s.desc}</span></span>
              </button>
            );
          })}
        </div>
        <div className="mt-4 flex flex-col gap-3 sm:flex-row sm:flex-wrap sm:items-end">
          <div className="w-full min-w-0 sm:w-[160px]"><label className={labelCls}>From (optional)</label><input type="date" value={start} max={end || undefined} onChange={(e) => setStart(e.target.value)} data-testid="audit-report-start" className={`mt-2 ${dateCls}`} /></div>
          <div className="w-full min-w-0 sm:w-[160px]"><label className={labelCls}>To (optional)</label><input type="date" value={end} min={start || undefined} onChange={(e) => setEnd(e.target.value)} data-testid="audit-report-end" className={`mt-2 ${dateCls}`} /></div>
          <button type="button" disabled={genSections.length === 0} onClick={generate} data-testid="audit-report-generate" className={`${btnGold} w-full sm:w-auto`}><Download className="h-4 w-4" /> Generate PDF</button>
        </div>
        {genSections.length === 0 && <p className="mt-2 text-xs text-[var(--a-text-dim)]">Select at least one section to generate a report.</p>}
      </Collapsible>

      {/* Generated audit reports (viewable / filterable / archivable) */}
      <Collapsible title="Generated audit reports" icon={FileText} count={reports ? activeReports.length : undefined} testId="audit-reports-card">
        <p className="text-sm text-[var(--a-text-muted)]">Every audit report you generate is saved here to view, download, or archive. Archived ones move to the Archive tab.</p>
        <div className="relative mt-4">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-[var(--a-text-dim)]" />
          <input value={repQ} onChange={(e) => setRepQ(e.target.value)} data-testid="audit-reports-search" placeholder="Search reports by scope, section, or who generated them…"
            className="w-full rounded-lg border border-[var(--a-border)] bg-[var(--a-field)] py-2 pl-9 pr-3 text-sm text-[var(--a-text)] placeholder:text-[var(--a-text-dim)] shadow-inner focus:border-[#D9A93E] focus:outline-none focus:ring-2 focus:ring-[#D9A93E]/40" />
        </div>
        <div className="mt-3 flex flex-wrap items-center gap-3" data-testid="audit-reports-filters">
          <AdminSelect value={repCat} onValueChange={setRepCat} testId="audit-reports-category" ariaLabel="Filter reports by category" triggerClassName="h-10 w-[190px]"
            options={[{ value: "all", label: "All categories" }, ...AUDIT_SECTIONS.map((s) => ({ value: s.id, label: s.label }))]} />
          <AdminSelect value={repDateMode} onValueChange={setRepDateMode} testId="audit-reports-date" ariaLabel="Filter reports by timeframe" triggerClassName="h-10 w-[190px]"
            options={[{ value: "all", label: "All time" }, { value: "24h", label: "Last 24 hours" }, { value: "2w", label: "Last two weeks" }, { value: "1m", label: "Last month" }]} />
        </div>
        {reports === null ? <p className="mt-4 text-sm text-[var(--a-text-dim)]">Loading…</p>
          : activeReports.length === 0 ? <p className="mt-4 text-sm text-[var(--a-text-dim)]" data-testid="audit-reports-empty">No saved audit reports yet — generate one above.</p>
          : filteredReports.length === 0 ? <p className="mt-4 text-sm text-[var(--a-text-dim)]">No reports match your search.</p>
          : <div className="mt-4 space-y-3">{filteredReports.map((r) => <AuditReportRow key={r.id} token={token} r={r} onArchive={setRepArchive} />)}</div>}
      </Collapsible>

      {/* Activity log */}
      {canFeed && (
      <Collapsible title="Activity log" icon={History} count={entries.length} testId="activity-log-card">
        <p className="text-sm text-[var(--a-text-muted)]">A plain-English record of what admins do in the portal — kept for accountability and security oversight.</p>
        <div className="relative mt-4">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-[var(--a-text-dim)]" />
          <input value={q} onChange={(e) => setQ(e.target.value)} data-testid="activity-search" placeholder="Search actions or admins…"
            className="w-full rounded-lg border border-[var(--a-border)] bg-[var(--a-field)] py-2 pl-9 pr-3 text-sm text-[var(--a-text)] placeholder:text-[var(--a-text-dim)] shadow-inner focus:border-[#D9A93E] focus:outline-none focus:ring-2 focus:ring-[#D9A93E]/40" />
        </div>
        <EqualStack className="mt-3" testId="activity-filters">
          <div className="grid grid-cols-2 gap-2 sm:flex sm:flex-wrap sm:items-center">
            <AdminSelect value={category} onValueChange={setCategory} testId="activity-category" ariaLabel="Filter by area" triggerClassName="h-10 w-full sm:w-[190px]"
              options={[{ value: "all", label: "All areas" }, ...(data?.categories || []).map((c) => ({ value: c.value, label: c.label }))]} />
            <AdminSelect value={actor} onValueChange={setActor} testId="activity-actor" ariaLabel="Filter by admin" triggerClassName="h-10 w-full sm:w-[190px]"
              options={[{ value: "all", label: "All admins" }, ...(data?.actors || []).map((a) => ({ value: a, label: a }))]} />
            <label className="flex min-w-0 flex-col gap-1">
              <span className="text-[0.6rem] font-semibold uppercase tracking-[0.14em] text-[var(--a-text-dim)]">From</span>
              <input type="date" value={start} max={end || undefined} onChange={(e) => setStart(e.target.value)} data-testid="activity-start" className="h-10 w-full min-w-0 rounded-lg border border-[var(--a-border)] bg-[var(--a-field)] px-3 text-sm text-[var(--a-text)] shadow-inner focus:border-[#D9A93E] focus:outline-none focus:ring-2 focus:ring-[#D9A93E]/40 sm:w-[160px]" aria-label="From date" />
            </label>
            <label className="flex min-w-0 flex-col gap-1">
              <span className="text-[0.6rem] font-semibold uppercase tracking-[0.14em] text-[var(--a-text-dim)]">To</span>
              <input type="date" value={end} min={start || undefined} onChange={(e) => setEnd(e.target.value)} data-testid="activity-end" className="h-10 w-full min-w-0 rounded-lg border border-[var(--a-border)] bg-[var(--a-field)] px-3 text-sm text-[var(--a-text)] shadow-inner focus:border-[#D9A93E] focus:outline-none focus:ring-2 focus:ring-[#D9A93E]/40 sm:w-[160px]" aria-label="To date" />
            </label>
            {hasFilter && <button type="button" onClick={() => { setCategory("all"); setActor("all"); setStart(""); setEnd(""); setQ(""); }} data-testid="activity-clear" className="inline-flex items-center justify-center gap-1 rounded-lg border border-[var(--a-border)] bg-[var(--a-field)] px-2.5 py-2 text-xs font-medium text-[var(--a-text2)] transition-colors hover:border-[#D9A93E]/50 hover:text-[var(--a-gold-ink)]"><X className="h-3.5 w-3.5" /> Clear</button>}
          </div>
        </EqualStack>
        {data === null ? <p className="mt-4 text-sm text-[var(--a-text-dim)]">Loading…</p>
          : entries.length === 0 ? <p className="mt-4 text-sm text-[var(--a-text-dim)]" data-testid="activity-empty">No matching activity.</p>
          : (
            <ul className="mt-4 space-y-2" data-testid="activity-list">
              {entries.map((e) => (
                <ActivityEntry key={e.id} e={e} catLabel={catLabel} />
              ))}
            </ul>
          )}
      </Collapsible>
      )}
      {!canFeed && (
        <p className="rounded-xl border border-dashed border-[var(--a-border)] bg-[var(--a-surface2)] px-4 py-3 text-sm text-[var(--a-text-muted)]" data-testid="activity-feed-restricted">
          The live activity feed isn&apos;t part of your access — you can still generate and review audit reports above.
        </p>
      )}
      {repArchive && <ArchiveSummaryModal title="Archive audit report" subtitle={`Audit report · ${repArchive.scope} — give a reason so the archive stays auditable. You can restore it anytime.`}
        onConfirm={async (reason) => { await archiveAuditReport(token, repArchive.id, true, reason); setRepArchive(null); setRepBump((n) => n + 1); }} onClose={() => setRepArchive(null)} />}
    </div>
  );
}


function DonationsView({ token, me }) {
  const can = (p) => me?.role === "master" || (me?.permissions || []).includes(p);
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const now = useMemo(() => new Date(), []);
  const curMonth = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`;
  // The manual summary can target any of the 12 completed months in the past (never the
  // current, still-in-progress month). Most-recent (previous month) first.
  const pastMonths = useMemo(() => {
    const arr = [];
    for (let i = 1; i <= 12; i++) {
      const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
      arr.push({ val: `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`, label: d.toLocaleString(undefined, { month: "long", year: "numeric" }) });
    }
    return arr;
  }, [now]);
  const [summaryMonth, setSummaryMonth] = useState(pastMonths[0].val);
  const summaryMonthLabel = pastMonths.find((m) => m.val === summaryMonth)?.label || pastMonths[0].label;
  const [goalMonth, setGoalMonth] = useState(curMonth);
  const [goalAmt, setGoalAmt] = useState("");
  const [savingGoal, setSavingGoal] = useState(false);
  const [rec, setRec] = useState(null);
  const [recLoading, setRecLoading] = useState(false);
  const [exportScope, setExportScope] = useState("all");
  const [exporting, setExporting] = useState("");
  const [msg, setMsg] = useState(null);

  const load = useCallback(() => {
    setLoading(true);
    getDonationReport(token, "all").then(setReport).catch(() => {}).finally(() => setLoading(false));
  }, [token]);
  useEffect(() => { load(); }, [load]);

  const monthOptions = useMemo(() => {
    const arr = [];
    for (let i = -12; i <= 12; i++) {
      const d = new Date(now.getFullYear(), now.getMonth() + i, 1);
      arr.push({ val: `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`, label: d.toLocaleString(undefined, { month: "long", year: "numeric" }) });
    }
    return arr;
  }, [now]);

  const existingGoal = report?.summary?.find((s) => s.month === goalMonth)?.goal;
  useEffect(() => { setGoalAmt(existingGoal != null ? String(existingGoal) : ""); }, [goalMonth, existingGoal]);
  useEffect(() => {
    let cancel = false;
    setRec(null); setRecLoading(true);
    getGoalRecommendation(token, goalMonth).then((d) => { if (!cancel) setRec(d); }).catch(() => {}).finally(() => { if (!cancel) setRecLoading(false); });
    return () => { cancel = true; };
  }, [token, goalMonth]);

  const money = (n) => `$${Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

  const saveGoal = async (e) => {
    e.preventDefault(); setMsg(null);
    const val = parseFloat(goalAmt);
    if (!(val >= 0)) return setMsg({ t: "err", m: "Enter a valid goal amount." });
    setSavingGoal(true);
    try {
      await setDonationGoal(token, goalMonth, val);
      setMsg({ t: "ok", m: `Goal for ${monthOptions.find((m) => m.val === goalMonth)?.label} set to ${money(val)}.` });
      load();
    } catch (err) { setMsg({ t: "err", m: errMsg(err, "Could not save goal.") }); }
    finally { setSavingGoal(false); }
  };

  const doExport = async (fmt) => {
    setExporting(fmt); setMsg(null);
    try {
      triggerDownload(donationsExportUrl(token, fmt, exportScope));
    } catch (err) { setMsg({ t: "err", m: errMsg(err, "Export failed.") }); }
    finally { setExporting(""); }
  };

  const [sendingReport, setSendingReport] = useState(false);
  const [summaryRefresh, setSummaryRefresh] = useState(0);
  const sendNow = async () => {
    setSendingReport(true); setMsg(null);
    try {
      const res = await sendMonthlyReport(token, summaryMonth);
      setMsg({ t: "ok", m: res.skipped ? "This summary was already sent." : `${summaryMonthLabel} summary emailed to ${res.count} recipient${res.count === 1 ? "" : "s"}.` });
      setSummaryRefresh((n) => n + 1);
    } catch (err) { setMsg({ t: "err", m: errMsg(err, "Could not send the summary.") }); }
    finally { setSendingReport(false); }
  };

  const scopeOptions = [{ val: "all", label: "All time" }, ...(report?.summary || []).map((s) => ({ val: s.month, label: s.label }))];

  return (
    <div data-testid="admin-donations" className="space-y-6">
      {msg && <p className={`rounded-lg border px-3 py-2 text-sm ${msg.t === "ok" ? "border-emerald-500/20 bg-emerald-500/10 text-emerald-300" : "border-rose-500/20 bg-rose-500/10 text-rose-300"}`} data-testid="donations-msg">{msg.m}</p>}

      <DonationsList token={token} />

      {/* Set monthly goal */}
      <Collapsible title="Set monthly goal" icon={Target} testId="donations-goal-card">
        <form onSubmit={saveGoal}>
        <p className="text-sm text-[var(--a-text-muted)]">Pick a month and enter its fundraising goal — any amount. This drives the donate page progress bar and the report below. Months with no goal set use a $350 default.</p>
        <div className="mt-4 flex flex-col gap-3 sm:flex-row sm:items-end">
          <div className="sm:w-56">
            <label className={labelCls}>Month</label>
            <AdminSelect value={goalMonth} onValueChange={setGoalMonth} testId="goal-month-select" triggerClassName="mt-2 h-11 w-full text-base"
              options={monthOptions.map((m) => ({ value: m.val, label: m.label }))} />
          </div>
          <div className="sm:w-48">
            <label className={labelCls}>Goal (USD)</label>
            <div className="relative mt-2">
              <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-[var(--a-text-dim)]">$</span>
              <Input type="number" min="0" step="any" value={goalAmt} onChange={(e) => setGoalAmt(e.target.value)} placeholder="350" data-testid="goal-amount-input" className={`${fieldCls} pl-7`} />
            </div>
          </div>
          <button type="submit" disabled={savingGoal} data-testid="goal-save-btn" className={btnGold}>{savingGoal ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Save className="h-4 w-4" /> Save goal</>}</button>
        </div>
        {/* Smart recommendation */}
        <div className="mt-4 rounded-xl border border-[#D9A93E]/35 bg-[#D9A93E]/[0.07] p-4" data-testid="goal-recommendation">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="flex items-center gap-3">
              <span className="grid h-11 w-11 shrink-0 place-items-center rounded-xl bg-[#D9A93E]/15 text-[var(--a-gold-ink)] ring-1 ring-[#D9A93E]/30"><Sparkles className="h-5 w-5" strokeWidth={1.75} /></span>
              <div>
                <p className="text-[0.68rem] font-semibold uppercase tracking-wide text-[var(--a-text-muted)]">Recommended goal · {rec?.label || monthOptions.find((m) => m.val === goalMonth)?.label}</p>
                <p className="font-heading text-2xl font-bold text-[var(--a-text)]" data-testid="recommended-goal-value">{recLoading || !rec ? "…" : money(rec.recommended)}</p>
              </div>
            </div>
            <button type="button" onClick={() => rec && setGoalAmt(String(rec.recommended))} disabled={!rec} data-testid="use-recommendation-btn" className={btnSecondary}><CheckCircle2 className="h-4 w-4" /> Use this goal</button>
          </div>
          {rec?.rationale?.length > 0 && (
            <ul className="mt-3 space-y-1.5">
              {rec.rationale.map((line, i) => (
                <li key={i} className="flex gap-2 text-xs text-[var(--a-text-muted)]"><span className="mt-1.5 h-1 w-1 shrink-0 rounded-full bg-[#D9A93E]" />{line}</li>
              ))}
            </ul>
          )}
          <p className="mt-3 text-[0.68rem] text-[var(--a-text-dim)]">A data-informed suggestion — you can accept it or enter your own amount.</p>
        </div>
        </form>
      </Collapsible>

      {/* Actual vs Goal */}
      <Collapsible title="Actual vs goal" icon={BarChart3} testId="donations-actual-card">
        <div className="flex flex-wrap items-center justify-end gap-3">
          {report && <span className="text-sm text-[var(--a-text-muted)]" data-testid="donations-total">Total raised {money(report.totals.raised)} · {report.totals.gifts} gift{report.totals.gifts === 1 ? "" : "s"}</span>}
        </div>
        {loading ? <div className="grid place-items-center py-10"><Loader2 className="h-6 w-6 animate-spin text-[var(--a-gold-ink)]" /></div>
          : (report?.summary?.length ? (
            <div className="mt-4 overflow-x-auto no-scrollbar">
              <table className="w-full min-w-[520px] text-sm" data-testid="donations-table">
                <thead><tr className="border-b border-[var(--a-border)] text-left text-xs uppercase tracking-wide text-[var(--a-text-dim)]">
                  <th className="whitespace-nowrap pb-2 pr-4 font-semibold">Month</th><th className="whitespace-nowrap pb-2 pr-4 font-semibold">Raised</th><th className="whitespace-nowrap pb-2 pr-4 font-semibold">Goal</th><th className="whitespace-nowrap pb-2 pr-4 font-semibold">Gifts</th><th className="whitespace-nowrap pb-2 font-semibold">Progress</th>
                </tr></thead>
                <tbody>
                  {report.summary.map((s) => {
                    const pct = s.pct == null ? 0 : Math.min(100, s.pct);
                    const hit = s.pct != null && s.pct >= 100;
                    return (
                      <tr key={s.month} className="border-b border-[var(--a-border)]" data-testid={`donations-row-${s.month}`}>
                        <td className="whitespace-nowrap py-3 pr-4 font-medium text-[var(--a-text2)]">{s.label}</td>
                        <td className="whitespace-nowrap py-3 pr-4 text-[var(--a-text2)]">{money(s.raised)}</td>
                        <td className="whitespace-nowrap py-3 pr-4 text-[var(--a-text-muted)]">{money(s.goal)}</td>
                        <td className="whitespace-nowrap py-3 pr-4 text-[var(--a-text-muted)]">{s.gifts}</td>
                        <td className="py-3">
                          <div className="flex items-center gap-2">
                            <div className="h-2 w-28 overflow-hidden rounded-full bg-[var(--a-surface2)]"><div className={`h-full rounded-full ${hit ? "bg-emerald-400" : "bg-[#D9A93E]"}`} style={{ width: `${pct}%` }} /></div>
                            <span className={`text-xs font-semibold ${hit ? "text-emerald-300" : "text-[var(--a-text2)]"}`}>{s.pct == null ? "—" : `${s.pct}%`}</span>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          ) : <p className="mt-4 text-sm text-[var(--a-text-dim)]">No donations or goals yet.</p>)}
      </Collapsible>

      {/* Export */}
      {can("reports_view") && (
      <Collapsible title="Export report" icon={Download} testId="donations-export-card">
        <p className="text-sm text-[var(--a-text-muted)]">Download an actual-vs-goal report with every donation. Pick a range and format.</p>
        <div className="mt-4 flex flex-col gap-3 sm:flex-row sm:items-end">
          <div className="sm:w-56">
            <label className={labelCls}>Range</label>
            <AdminSelect value={exportScope} onValueChange={setExportScope} testId="export-scope-select" triggerClassName="mt-2 h-11 w-full text-base"
              options={scopeOptions.map((s) => ({ value: s.val, label: s.label }))} />
          </div>
          <div className="flex flex-wrap gap-2">
            <button type="button" onClick={() => doExport("pdf")} disabled={!!exporting} data-testid="export-pdf-btn" className={btnGold}>{exporting === "pdf" ? <Loader2 className="h-4 w-4 animate-spin" /> : <><FileText className="h-4 w-4" /> PDF</>}</button>
            <button type="button" onClick={() => doExport("csv")} disabled={!!exporting} data-testid="export-csv-btn" className={btnSecondary}>{exporting === "csv" ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Download className="h-4 w-4" /> CSV</>}</button>
            <button type="button" onClick={() => doExport("excel")} disabled={!!exporting} data-testid="export-excel-btn" className={btnSecondary}>{exporting === "excel" ? <Loader2 className="h-4 w-4 animate-spin" /> : <><FileSpreadsheet className="h-4 w-4" /> Excel</>}</button>
          </div>
        </div>
      </Collapsible>
      )}

      {/* Email a past month's summary (reports_send) */}
      {can("reports_send") && (
      <Collapsible title="Email a monthly summary" icon={Send} testId="donations-email-card">
        <p className="text-sm text-[var(--a-text-muted)]">Pick any completed month from the past 12 months and email its donation &amp; submissions PDF summaries to all admins. The current, still-in-progress month can&apos;t be sent. (This also runs automatically on the 1st of each month for the prior month.)</p>
        <div className="mt-4 flex flex-col gap-3 sm:flex-row sm:items-end">
          <div className="sm:w-64">
            <label className={labelCls}>Month to send</label>
            <AdminSelect value={summaryMonth} onValueChange={setSummaryMonth} testId="summary-month-select" ariaLabel="Choose the month to email" triggerClassName="mt-2 h-11 w-full text-base"
              options={pastMonths.map((m) => ({ value: m.val, label: m.label }))} />
          </div>
          <button type="button" onClick={sendNow} disabled={sendingReport} data-testid="send-monthly-btn" className={btnGold}>{sendingReport ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Send className="h-4 w-4" /> Send {summaryMonthLabel} summary</>}</button>
        </div>
      </Collapsible>
      )}

      {/* Summary email history (reports_view) */}
      {can("reports_view") && <SummaryHistory token={token} refreshKey={summaryRefresh} />}
    </div>
  );
}

/* ============================ Settings ============================ */
function SettingsView({ token }) {
  const [cur, setCur] = useState(""); const [nw, setNw] = useState(""); const [conf, setConf] = useState("");
  const [msg, setMsg] = useState(null); const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault(); setMsg(null);
    if (!pwdValid(nw)) return setMsg({ t: "err", m: "New password doesn't meet the requirements below." });
    if (nw !== conf) return setMsg({ t: "err", m: "New passwords don't match." });
    setBusy(true);
    try { await changePassword(token, cur, nw); setMsg({ t: "ok", m: "Password updated successfully." }); setCur(""); setNw(""); setConf(""); }
    catch (err) { setMsg({ t: "err", m: errMsg(err, "Could not update password.") }); }
    finally { setBusy(false); }
  };
  return (
    <div data-testid="admin-settings" className="max-w-md">
      <form onSubmit={submit} className={`${card} p-6`}>
        <h3 className="flex items-center gap-2 font-heading text-lg font-semibold tracking-tight"><KeyRound className="h-5 w-5 text-[var(--a-gold-ink)]" strokeWidth={1.75} /> Change password</h3>
        <label className={`${labelCls} mt-5`}>Current password</label>
        <PasswordInput testId="settings-current" value={cur} onChange={(e) => setCur(e.target.value)} className={`mt-2 ${fieldCls}`} />
        <label className={`${labelCls} mt-4`}>New password</label>
        <PasswordInput testId="settings-new" value={nw} onChange={(e) => setNw(e.target.value)} className={`mt-2 ${fieldCls}`} />
        <PolicyChecklist pw={nw} />
        <label className={`${labelCls} mt-4`}>Confirm new password</label>
        <PasswordInput testId="settings-confirm" value={conf} onChange={(e) => setConf(e.target.value)} className={`mt-2 ${fieldCls}`} />
        {msg && <p className={`mt-4 rounded-lg border px-3 py-2 text-sm ${msg.t === "ok" ? "border-emerald-500/20 bg-emerald-500/10 text-emerald-300" : "border-rose-500/20 bg-rose-500/10 text-rose-300"}`} data-testid="settings-msg">{msg.m}</p>}
        <button type="submit" disabled={busy} data-testid="settings-submit" className={`mt-5 ${btnGold}`}>{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Update Password"}</button>
      </form>
    </div>
  );
}

/* ============================ Forced first-login password change ============================ */
function ForcePasswordChange({ token, initialCurrent, email, onDone, onLogout, theme, onToggleTheme }) {
  const [cur, setCur] = useState(initialCurrent || "");
  const [nw, setNw] = useState(""); const [conf, setConf] = useState("");
  const [msg, setMsg] = useState(""); const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault(); setMsg("");
    if (!pwdValid(nw)) return setMsg("Your new password doesn't meet the requirements below.");
    if (nw !== conf) return setMsg("New passwords don't match.");
    setBusy(true);
    try { await changePassword(token, cur, nw); onDone(); }
    catch (err) { setMsg(errMsg(err, "Could not update password.")); }
    finally { setBusy(false); }
  };
  return (
    <AuthLayout icon={KeyRound} title="Set a new password" subtitle={`For your security, please replace the temporary password provided for ${email}.`} testId="auth-force-wrap" theme={theme} onToggleTheme={onToggleTheme}>
      <form onSubmit={submit} className="mt-6" data-testid="force-password-form">
        {!initialCurrent && (<>
          <label className={labelCls}>Temporary password</label>
          <PasswordInput testId="force-current" value={cur} onChange={(e) => setCur(e.target.value)} className={`mt-2 mb-4 ${fieldCls}`} />
        </>)}
        <label className={labelCls}>New password</label>
        <PasswordInput testId="force-new" value={nw} onChange={(e) => setNw(e.target.value)} className={`mt-2 ${fieldCls}`} />
        <PolicyChecklist pw={nw} />
        <label className={`${labelCls} mt-4`}>Confirm new password</label>
        <PasswordInput testId="force-confirm" value={conf} onChange={(e) => setConf(e.target.value)} className={`mt-2 ${fieldCls}`} />
        {msg && <p className="mt-4 rounded-lg border border-rose-500/20 bg-rose-500/10 px-3 py-2 text-sm text-rose-300" data-testid="force-msg">{msg}</p>}
        <button type="submit" disabled={busy} data-testid="force-submit" className={`mt-6 w-full ${btnGold}`}>{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save & Continue"}</button>
        <button type="button" onClick={onLogout} className="mt-3 w-full text-center text-xs text-[var(--a-text-dim)] transition-colors hover:text-[var(--a-text2)]">Sign out</button>
      </form>
    </AuthLayout>
  );
}

/* ============================ Shell ============================ */
function ArchiveCatFilter({ value, onChange, options, testId }) {
  // A neat right-aligned category dropdown shown above an archive section.
  return (
    <div className="mb-4 flex items-center justify-end gap-2">
      <span className="text-xs font-medium uppercase tracking-wide text-[var(--a-text-dim)]">Category</span>
      <AdminSelect value={value} onValueChange={onChange} testId={testId} ariaLabel="Filter by category"
        triggerClassName="h-9 w-[180px]" options={options} />
    </div>
  );
}

function ArchiveView({ token, me, onChange }) {
  const master = me?.role === "master";
  const p = me?.permissions || [];
  const canArch = (sub) => master || p.includes("archive") || p.includes(sub);
  const showSubs = canArch("archive_submissions");
  const showReports = canArch("archive_reports") && (master || p.includes("reports_view") || p.includes("archive"));
  const showSummaries = canArch("archive_summaries") && (master || p.includes("reports_view") || p.includes("archive"));
  const showAudit = canArch("archive_audit") && (master || p.includes("audit") || p.some((x) => x.startsWith("audit_")) || p.includes("archive"));
  const [subs, setSubs] = useState(showSubs ? null : []);
  const [reports, setReports] = useState(showReports ? null : []);
  const [summaries, setSummaries] = useState(showSummaries ? null : []);
  const [auditReports, setAuditReports] = useState(showAudit ? null : []);
  const [historyFor, setHistoryFor] = useState(null);
  const [subCat, setSubCat] = useState("all");
  const [repCat, setRepCat] = useState("all");
  const load = useCallback(() => {
    if (showSubs) getSubmissions(token).then((d) => setSubs((d.submissions || []).filter((s) => s.archived))).catch(() => setSubs([]));
    if (showReports) getReports(token).then((d) => setReports((d.reports || []).filter((r) => r.archived))).catch(() => setReports([]));
    if (showSummaries) getSummaries(token).then((d) => setSummaries((d.summaries || []).filter((r) => r.archived))).catch(() => setSummaries([]));
    if (showAudit) getAuditReports(token).then((d) => setAuditReports((d.reports || []).filter((r) => r.archived))).catch(() => setAuditReports([]));
  }, [token, showSubs, showReports, showSummaries, showAudit]);
  useEffect(() => { load(); }, [load]);
  const unSub = async (s) => { try { await unarchiveSubmission(token, s.id); load(); onChange && onChange(); } catch { /* noop */ } };
  const unSummary = async (r) => { try { await archiveSummary(token, r.id, false, ""); load(); } catch { /* noop */ } };
  const unAuditReport = async (r) => { try { await archiveAuditReport(token, r.id, false, ""); load(); } catch { /* noop */ } };
  const onReportArchive = async (report, archived) => { try { await archiveReport(token, report.id, archived, ""); load(); onChange && onChange(); } catch { /* noop */ } };
  const reportGroups = [
    { id: "submissions", label: "Submission reports", icon: Inbox },
    { id: "donations", label: "Donation reports", icon: HeartHandshake },
  ];

  // Only offer categories that are actually present in the archive.
  const subCatsPresent = CATEGORIES.filter((c) => (subs || []).some((s) => s.type === c.id));
  const shownSubs = (subs || []).filter((s) => subCat === "all" || s.type === subCat);
  const subFilterOpts = [{ value: "all", label: "All categories" }, ...subCatsPresent.map((c) => ({ value: c.id, label: c.label }))];

  const repGroupsPresent = reportGroups.filter((g) => (reports || []).some((r) => r.category === g.id));
  const shownReportGroups = repGroupsPresent.filter((g) => repCat === "all" || g.id === repCat);
  const repFilterOpts = [{ value: "all", label: "All categories" }, ...repGroupsPresent.map((g) => ({ value: g.id, label: g.label }))];

  return (
    <div className="space-y-6" data-testid="admin-archive">
      {showSubs && (
      <Collapsible title="Archived submissions" icon={Archive} count={subs ? subs.length : undefined} testId="archive-subs">
        {subs === null ? <p className="text-sm text-[var(--a-text-muted)]">Loading…</p>
          : subs.length === 0 ? <p className="text-sm text-[var(--a-text-muted)]">No archived submissions.</p>
          : (
            <>
              {subCatsPresent.length > 0 && <ArchiveCatFilter value={subCat} onChange={setSubCat} options={subFilterOpts} testId="archive-sub-category" />}
              {shownSubs.length === 0 ? <p className="text-sm text-[var(--a-text-muted)]">No archived submissions in this category.</p> : (
                <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
                  {shownSubs.map((s) => (
                    <div key={s.id} className="flex min-w-0 flex-col rounded-xl border border-[var(--a-border)] bg-[var(--a-surface2)] p-4" data-testid={`archived-sub-${s.id}`}>
                      <div className="flex items-start justify-between gap-2">
                        <span className={`rounded-md px-2 py-0.5 text-[0.65rem] font-semibold uppercase tracking-wide ring-1 ${TYPE_COLOR[s.type] || "bg-[var(--a-hover)] text-[var(--a-text2)] ring-[var(--a-border)]"}`}>{TYPE_LABEL[s.type] || s.type}</span>
                        <StatusPill status={s.status} onClick={() => setHistoryFor(s)} testId={`archived-status-${s.id}`} />
                      </div>
                      <h4 className="mt-2 font-heading text-sm font-semibold text-[var(--a-text)]">{displayName(s)}</h4>
                      <p className="text-xs text-[var(--a-text-dim)] [overflow-wrap:anywhere]">{s.email || "—"}</p>
                      <time className="mt-0.5 text-xs text-[var(--a-text-dim)]">{fmtDate(s.created_at)}</time>
                      <button onClick={() => unSub(s)} data-testid={`unarchive-${s.id}`} className={`${btnSecondary} mt-3 w-full`}><ArchiveRestore className="h-4 w-4" /> Restore</button>
                    </div>
                  ))}
                </div>
              )}
            </>
          )}
      </Collapsible>
      )}
      {showReports && (
        <Collapsible title="Archived reports" icon={Archive} count={reports ? reports.length : undefined} testId="archive-reports">
          {reports === null ? <p className="text-sm text-[var(--a-text-muted)]">Loading…</p>
            : reports.length === 0 ? <p className="text-sm text-[var(--a-text-muted)]">No archived reports.</p>
            : (
              <>
                {repGroupsPresent.length > 0 && <ArchiveCatFilter value={repCat} onChange={setRepCat} options={repFilterOpts} testId="archive-report-category" />}
                <div className="space-y-6">
                  {shownReportGroups.map((g) => {
                    const items = (reports || []).filter((r) => r.category === g.id);
                    return (
                      <div key={g.id} data-testid={`archive-reports-${g.id}`}>
                        <h4 className="mb-2 flex items-center gap-2 text-sm font-semibold text-[var(--a-text2)]"><g.icon className="h-4 w-4 text-[var(--a-gold-ink)]" strokeWidth={1.75} /> {g.label} <span className="font-normal text-[var(--a-text-dim)]">({items.length})</span></h4>
                        <div className="space-y-3">{items.map((r) => <ReportRow key={r.id} token={token} report={r} onArchive={onReportArchive} />)}</div>
                      </div>
                    );
                  })}
                </div>
              </>
            )}
        </Collapsible>
      )}
      {showSummaries && (
        <Collapsible title="Archived summary emails" icon={Archive} count={summaries ? summaries.length : undefined} testId="archive-summaries">
          {summaries === null ? <p className="text-sm text-[var(--a-text-muted)]">Loading…</p>
            : summaries.length === 0 ? <p className="text-sm text-[var(--a-text-muted)]">No archived summary emails.</p>
            : (
              <div className="space-y-3">
                {summaries.map((r) => <SummaryRow key={r.id} token={token} r={r} onRestore={unSummary} />)}
              </div>
            )}
        </Collapsible>
      )}
      {showAudit && (
        <Collapsible title="Archived audit reports" icon={ShieldCheck} count={auditReports ? auditReports.length : undefined} testId="archive-audit-reports">
          {auditReports === null ? <p className="text-sm text-[var(--a-text-muted)]">Loading…</p>
            : auditReports.length === 0 ? <p className="text-sm text-[var(--a-text-muted)]">No archived audit reports.</p>
            : <div className="space-y-3">{auditReports.map((r) => <AuditReportRow key={r.id} token={token} r={r} onRestore={unAuditReport} />)}</div>}
        </Collapsible>
      )}
      {historyFor && <HistoryModal submission={historyFor} onClose={() => setHistoryFor(null)} />}
    </div>
  );
}


export default function Admin() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [token, setToken] = useState(() => {
    // Arriving via a director card (?fresh=1) — always start a brand-new secure login (no piggybacking)
    if (new URLSearchParams(window.location.search).get("fresh") === "1") {
      sessionStorage.removeItem("tf_admin_token");
      return "";
    }
    return sessionStorage.getItem("tf_admin_token") || "";
  });
  const [me, setMe] = useState(null);
  const [checking, setChecking] = useState(!!token);
  const [view, setView] = useState("submissions");
  const [loginPw, setLoginPw] = useState("");
  const [menuOpen, setMenuOpen] = useState(false);
  const [adminTheme, setAdminTheme] = useState(() => sessionStorage.getItem("tf-admin-theme") || "light");
  const toggleAdminTheme = () => setAdminTheme((t) => (t === "dark" ? "light" : "dark"));
  // Scope the admin theme to the body while this page is mounted. Session-scoped so it
  // survives in-session refreshes but resets to dark once the browser/tab is closed.
  useEffect(() => {
    sessionStorage.setItem("tf-admin-theme", adminTheme);
    document.body.classList.add("admin-scope");
    document.body.classList.toggle("admin-light", adminTheme === "light");
    return () => { document.body.classList.remove("admin-scope", "admin-light"); };
  }, [adminTheme]);

  // Strip the one-time ?fresh flag so a later refresh doesn't force re-login again
  useEffect(() => {
    if (searchParams.get("fresh") === "1") {
      const next = new URLSearchParams(searchParams);
      next.delete("fresh");
      setSearchParams(next, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  useEffect(() => {
    if (!token) return;
    adminMe(token).then(setMe).catch(() => { sessionStorage.removeItem("tf_admin_token"); setToken(""); setMe(null); }).finally(() => setChecking(false));
  }, [token]);

  const onLogin = (data, pw) => { sessionStorage.setItem("tf_admin_token", data.token); setToken(data.token); setMe(data); setLoginPw(pw || ""); setChecking(false); };
  const logout = () => { sessionStorage.removeItem("tf_admin_token"); setToken(""); setMe(null); setLoginPw(""); setView("submissions"); navigate("/"); };

  // Live counts for the sidebar: "needs attention" badge + whether an Archive tab should appear.
  const [attnCount, setAttnCount] = useState(0);
  const [archiveInfo, setArchiveInfo] = useState({ subs: 0, reports: 0, summaries: 0, audits: 0 });
  const [refreshKey, setRefreshKey] = useState(0);
  const refreshCounts = () => setRefreshKey((k) => k + 1);
  useEffect(() => {
    if (!token || !me || me.must_change_password) return;
    const perms = me.permissions || [];
    const master = me.role === "master";
    const hasReports = master || perms.includes("reports_view");
    const hasArchAny = (sub) => master || perms.includes("archive") || perms.includes(sub);
    const hasAudit = master || perms.includes("audit") || perms.some((x) => x.startsWith("audit_"));
    getSubmissions(token).then((d) => {
      const subs = d.submissions || [];
      setAttnCount(subs.filter((s) => !s.archived && s.status !== "closed").length);
      setArchiveInfo((a) => ({ ...a, subs: hasArchAny("archive_submissions") ? subs.filter((s) => s.archived).length : 0 }));
    }).catch(() => {});
    if (hasReports && hasArchAny("archive_reports")) {
      getReports(token).then((d) => setArchiveInfo((a) => ({ ...a, reports: (d.reports || []).filter((r) => r.archived).length }))).catch(() => {});
    }
    if (hasReports && hasArchAny("archive_summaries")) {
      getSummaries(token).then((d) => setArchiveInfo((a) => ({ ...a, summaries: (d.summaries || []).filter((r) => r.archived).length }))).catch(() => {});
    }
    if (hasAudit && hasArchAny("archive_audit")) {
      getAuditReports(token).then((d) => setArchiveInfo((a) => ({ ...a, audits: (d.reports || []).filter((r) => r.archived).length }))).catch(() => {});
    }
  }, [token, me, view, refreshKey]);

  if (!token || (checking && !me)) {
    if (checking) return <div className="grid min-h-screen place-items-center bg-[var(--a-bg)] text-[var(--a-text)]"><Loader2 className="h-8 w-8 animate-spin text-[var(--a-gold-ink)]" /></div>;
    return <LoginView onLogin={onLogin} theme={adminTheme} onToggleTheme={toggleAdminTheme} />;
  }
  if (!me) return <LoginView onLogin={onLogin} theme={adminTheme} onToggleTheme={toggleAdminTheme} />;

  if (me.must_change_password) {
    return <ForcePasswordChange token={token} initialCurrent={loginPw} email={me.email} theme={adminTheme} onToggleTheme={toggleAdminTheme}
      onDone={() => { setMe({ ...me, must_change_password: false }); setLoginPw(""); }} onLogout={logout} />;
  }

  const isMaster = me.role === "master";
  const _perms = me.permissions || [];
  const can = (p) => {
    if (isMaster || _perms.includes(p)) return true;
    if (p === "audit") return _perms.some((x) => x.startsWith("audit_"));
    if (p === "archive") return _perms.some((x) => x.startsWith("archive_"));
    return false;
  };
  const nav = [
    { id: "submissions", label: "Submissions", icon: Inbox },
    { id: "attention", label: "Needs attention", icon: AlertTriangle },
    ...(can("donations") ? [{ id: "donations", label: "Donations", icon: Target }] : []),
    ...(can("reports_view") ? [{ id: "reports", label: "Reports", icon: FileText }] : []),
    ...(archiveInfo.subs > 0 || archiveInfo.reports > 0 || archiveInfo.summaries > 0 || archiveInfo.audits > 0 ? [{ id: "archive", label: "Archive", icon: Archive }] : []),
    ...(can("audit") ? [{ id: "activity", label: "Activity log", icon: History }] : []),
    ...(isMaster ? [{ id: "admins", label: "Admins", icon: Users }] : []),
    { id: "settings", label: "Settings", icon: Settings },
  ];
  const title = view === "admins" ? "Manage Admins" : view === "settings" ? "Settings" : view === "donations" ? "Donations" : view === "reports" ? "Reports" : view === "attention" ? "Needs attention" : view === "archive" ? "Archive" : view === "activity" ? "Activity log" : "Submissions";
  const go = (id) => { setView(id); setMenuOpen(false); };

  const SidebarContent = (
    <>
      <div className="flex justify-center border-b border-[var(--a-border)] px-3 pb-5 pt-6">
        <img src={adminTheme === "dark" ? "/tf-admin-logo-dark.png" : "/tf-admin-logo-light.png"} alt="Thefrent Foundation" data-testid="admin-sidebar-logo" draggable={false} className="w-[88%] max-w-[210px]" />
      </div>
      <div className="flex items-center gap-3 px-5 py-5">
        <span className="grid h-10 w-10 place-items-center rounded-xl bg-gradient-to-br from-[#D9A93E]/25 to-[#D9A93E]/5 text-[var(--a-gold-ink)] ring-1 ring-[#D9A93E]/30 shadow-[0_0_18px_-6px_rgba(217,169,62,0.5)]"><ShieldCheck className="h-5 w-5" strokeWidth={1.75} /></span>
        <div><p className="font-heading text-sm font-semibold leading-tight text-[var(--a-text)]">Admin Portal</p><p className="text-[0.7rem] text-[var(--a-text-dim)]">Thefrent Foundation</p></div>
      </div>
      <nav className="flex flex-col gap-1 px-3 py-2">
        {nav.map((n) => (
          <button key={n.id} onClick={() => go(n.id)} data-testid={`admin-nav-${n.id}`}
            className={`flex items-center gap-3 rounded-lg border-l-2 px-3.5 py-2.5 text-sm font-medium transition-colors duration-200 ${view === n.id ? "border-[#D9A93E] bg-gradient-to-r from-[#D9A93E]/15 to-transparent text-[var(--a-gold-ink)]" : "border-transparent text-[var(--a-text-muted)] hover:bg-[var(--a-chip)] hover:text-[var(--a-text)]"}`}>
            <n.icon className={`h-4 w-4 shrink-0 transition-colors ${view === n.id ? "text-[var(--a-gold-ink)]" : ""}`} strokeWidth={1.75} />
            <span className="flex-1 text-left">{n.label}</span>
            {n.id === "attention" && attnCount > 0 && (
              <span data-testid="attention-badge" className="grid h-5 min-w-[1.25rem] place-items-center rounded-full bg-[#D9A93E] px-1.5 text-[0.68rem] font-bold text-[#050B14]">{attnCount}</span>
            )}
          </button>
        ))}
      </nav>
      <div className="mt-auto border-t border-[var(--a-border)] p-4">
        <p className="truncate text-xs font-medium text-[var(--a-text2)]">{me.email}</p>
        <p className="mb-3 text-[0.62rem] uppercase tracking-wide text-[var(--a-gold-ink)]">{me.role}</p>
        <button onClick={logout} data-testid="admin-logout" className="inline-flex w-full items-center justify-center gap-2 rounded-lg border border-[var(--a-border)] px-4 py-2 text-xs font-semibold text-[var(--a-text2)] transition-colors hover:border-[#D9A93E]/50 hover:text-[var(--a-gold-ink)]"><LogOut className="h-4 w-4" /> Sign out</button>
      </div>
    </>
  );

  return (
    <div className="min-h-screen bg-[var(--a-bg)] text-[var(--a-text)]" data-testid="admin-dashboard">
      {/* Mobile top bar */}
      <div className="sticky top-0 z-30 flex h-14 items-center justify-between border-b border-[var(--a-border)] bg-[var(--a-backdrop)] px-4 backdrop-blur-xl lg:hidden">
        <button onClick={() => setMenuOpen(true)} aria-label="Open menu" data-testid="admin-menu-open" className="grid h-9 w-9 place-items-center rounded-lg text-[var(--a-text2)] transition-colors hover:bg-[var(--a-hover)]"><Menu className="h-5 w-5" /></button>
        <div className="flex items-center gap-2"><ShieldCheck className="h-4 w-4 text-[var(--a-gold-ink)]" /><span className="font-heading text-sm font-semibold">{title}</span></div>
        <div className="flex items-center gap-1.5">
          <AdminThemeToggle theme={adminTheme} onToggle={toggleAdminTheme} />
          <button onClick={logout} aria-label="Sign out" data-testid="admin-logout-mobile" className="grid h-9 w-9 place-items-center rounded-lg text-[var(--a-text2)] transition-colors hover:bg-[var(--a-hover)]"><LogOut className="h-4 w-4" /></button>
        </div>
      </div>

      {/* Mobile drawer */}
      {menuOpen && <div className="fixed inset-0 z-40 bg-black/60 backdrop-blur-sm lg:hidden" onClick={() => setMenuOpen(false)} />}
      <aside className={`fixed inset-y-0 left-0 z-50 flex w-64 flex-col overflow-y-auto no-scrollbar border-r border-[var(--a-border)] bg-gradient-to-b from-[var(--a-sidebar)] to-[var(--a-bg)] transition-transform duration-200 lg:translate-x-0 ${menuOpen ? "translate-x-0" : "-translate-x-full"}`}>
        <button onClick={() => setMenuOpen(false)} aria-label="Close menu" className="absolute right-3 top-3 grid h-8 w-8 place-items-center rounded-lg text-[var(--a-text-muted)] hover:bg-[var(--a-hover)] lg:hidden"><X className="h-4 w-4" /></button>
        {SidebarContent}
      </aside>

      {/* Main */}
      <div className="lg:pl-64">
        <header className="sticky top-0 z-20 hidden h-16 items-center justify-between border-b border-[var(--a-border)] bg-[var(--a-backdrop)] px-8 backdrop-blur-xl lg:flex">
          <h1 className="font-heading text-xl font-semibold tracking-tight text-[var(--a-text)]">{title}</h1>
          <div className="flex items-center gap-3">
            <AdminThemeToggle theme={adminTheme} onToggle={toggleAdminTheme} />
            <div className="flex items-center gap-3 rounded-full border border-[var(--a-border)] bg-[var(--a-chip)] py-1.5 pl-4 pr-1.5 shadow-sm transition-colors hover:border-[#D9A93E]/40" data-testid="admin-identity-chip">
              <div className="text-right">
                <p className="text-sm font-medium text-[var(--a-text2)]">{me.email}</p>
                <p className="text-[0.65rem] uppercase tracking-wide text-[var(--a-gold-ink)]">{me.role}</p>
              </div>
              <span className="grid h-9 w-9 place-items-center rounded-full bg-gradient-to-br from-[#D9A93E]/30 to-[var(--a-hover)] text-sm font-semibold text-[var(--a-gold-ink)] ring-1 ring-[#D9A93E]/25">{me.email?.[0]?.toUpperCase()}</span>
            </div>
          </div>
        </header>
        <main className="mx-auto max-w-7xl px-5 py-6 lg:px-8 lg:py-8">
          {view === "submissions" && <SubmissionsView token={token} me={me} />}
          {view === "attention" && <SubmissionsView token={token} me={me} attention />}
          {view === "donations" && (can("donations") ? <DonationsView token={token} me={me} /> : <AccessDenied onBack={() => setView("submissions")} />)}
          {view === "reports" && (can("reports_view") ? <ReportsView token={token} /> : <AccessDenied onBack={() => setView("submissions")} />)}
          {view === "archive" && (can("archive") || can("audit") ? <ArchiveView token={token} me={me} onChange={refreshCounts} /> : <AccessDenied onBack={() => setView("submissions")} />)}
          {view === "activity" && (can("audit") ? <ActivityLogView token={token} me={me} /> : <AccessDenied onBack={() => setView("submissions")} />)}
          {view === "admins" && (isMaster ? <AdminsView token={token} /> : <AccessDenied onBack={() => setView("submissions")} />)}
          {view === "settings" && <SettingsView token={token} />}
        </main>
      </div>
    </div>
  );
}
